import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import { YATAS_GROUP_BRAND_IDS, filterRowsByYatasCampaignName, matchesYatasCampaignNameFilter } from '../../utils/yatasCampaignNameFilter';
import { aggregateCombinedCampaignPerformanceRows } from '../../utils/googleAdsAggregateCombinedPerformance';
import '../../context/AuthContext.css';
import { Card } from '../../components/ui/Card';
import Ga4WebOnlyNote from '../../components/Ga4WebOnlyNote';
import DateRangeFilter from '../../components/filters/DateRangeFilter';
import { LineChart, BarChart } from '../../components/charts';
import { 
  DollarSign, 
  TrendingUp, 
  MousePointerClick, 
  Target,
  Filter,
  RefreshCw,
  ShoppingCart,
  Search,
  LayoutGrid,
  ShoppingBag,
  Zap,
  X,
  Monitor,
  Smartphone,
  Tablet,
  Play,
  Globe,
  MapPin,
  Lightbulb,
  HelpCircle,
  Calculator,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowUp,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ResponsiveContainer, ComposedChart, Bar, ReferenceLine, PieChart, Pie, Cell, Tooltip as TooltipRecharts, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend } from 'chart.js';
import { ResponsiveLine } from '@nivo/line';
import { Select } from 'antd';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Chart.js'in gerekli bileşenlerini kaydet
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);

// Update campaign types
const campaignTypes = {
  'AdvertisingChannelType.SEARCH': {
    icon: <Search className="w-4 h-4" />,
    color: 'blue',
    label: 'Search'
  },
  'AdvertisingChannelType.PERFORMANCE_MAX': {
    icon: <Zap className="w-4 h-4" />,
    color: 'purple',
    label: 'Performance Max'
  },
  'AdvertisingChannelType.DISPLAY': {
    icon: <LayoutGrid className="w-4 h-4" />,
    color: 'green',
    label: 'Display'
  },
  'AdvertisingChannelType.DEMAND_GEN': {
    icon: <Target className="w-4 h-4" />,
    color: 'pink',
    label: 'Demand Gen'
  },
  'AdvertisingChannelType.SHOPPING': {
    icon: <ShoppingBag className="w-4 h-4" />,
    color: 'yellow',
    label: 'Shopping'
  },
  'AdvertisingChannelType.VIDEO': {
    icon: <Play className="w-4 h-4" />,
    color: 'red',
    label: 'Video'
  },
  'AdvertisingChannelType.HOTEL': {
    icon: <MapPin className="w-4 h-4" />,
    color: 'indigo',
    label: 'Hotel'
  },
  'AdvertisingChannelType.MULTI_CHANNEL': {
    icon: <Globe className="w-4 h-4" />,
    color: 'teal',
    label: 'Multi Channel'
  },
  'AdvertisingChannelType.SMART': {
    icon: <Lightbulb className="w-4 h-4" />,
    color: 'amber',
    label: 'Smart'
  },
  'AdvertisingChannelType.LOCAL_SERVICES': {
    icon: <MapPin className="w-4 h-4" />,
    color: 'cyan',
    label: 'Local Services'
  },
  'AdvertisingChannelType.TRAVEL': {
    icon: <Globe className="w-4 h-4" />,
    color: 'blue',
    label: 'Travel'
  },
  'search': {
    icon: <Search className="w-4 h-4" />,
    color: 'blue',
    label: 'Search'
  },
  'Other': {
    icon: <Globe className="w-4 h-4" />,
    color: 'gray',
    label: 'Other'
  },
  'Unknown': {
    icon: <HelpCircle className="w-4 h-4" />,
    color: 'gray',
    label: 'Unknown'
  },
  'display': {
    icon: <LayoutGrid className="w-4 h-4" />,
    color: 'green',
    label: 'Display'
  },
  'shopping': {
    icon: <ShoppingBag className="w-4 h-4" />,
    color: 'yellow',
    label: 'Shopping'
  },
  'performance_max': {
    icon: <Zap className="w-4 h-4" />,
    color: 'purple',
    label: 'Performance Max'
  },
  'dsa': {
    icon: <Globe className="w-4 h-4" />,
    color: 'orange',
    label: 'Dynamic Search'
  },
  'video': {
    icon: <Play className="w-4 h-4" />,
    color: 'red',
    label: 'Video'
  },
  'app': {
    icon: <Smartphone className="w-4 h-4" />,
    color: 'indigo',
    label: 'App'
  },
  'TOTAL': {
    icon: <BarChart3 className="w-4 h-4" />,
    color: 'gray',
    label: 'Total'
  },
  // Veritabanında görülen diğer kampanya türlerini ekleyin
  'google': {
    icon: <Search className="w-4 h-4" />,
    color: 'blue',
    label: 'Google'
  },
  'cpc': {
    icon: <MousePointerClick className="w-4 h-4" />,
    color: 'green',
    label: 'CPC'
  },
  '(not set)': {
    icon: <HelpCircle className="w-4 h-4" />,
    color: 'gray',
    label: 'Not Set'
  },
  '(direct)': {
    icon: <Target className="w-4 h-4" />,
    color: 'purple',
    label: 'Direct'
  },
  '{{placement}}': {
    icon: <LayoutGrid className="w-4 h-4" />,
    color: 'orange',
    label: 'Placement'
  }
};

// Function to format numbers for display - cost değerleri için tam rakam gösterme
const formatNumber = (value, options = {}) => {
  if (value === undefined || value === null) return '0';
  
  // Convert string to number if needed
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number
  if (isNaN(num)) return '0';
  
  const { style, decimals = 1, isCost = false } = options;
  
  // Format as currency if specified
  if (style === 'currency') {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  }
  
  // Cost değerleri için tam rakam göster ve nokta ile ayır
  if (isCost) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(num));
  }
  
  // Format large numbers with K, M suffix
  if (num >= 1000000) {
    return (num / 1000000).toFixed(decimals) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(decimals) + 'K';
  } else {
    return num.toFixed(decimals);
  }
};

// Function to format percentage values
const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  
  // Convert string to number if needed
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number
  if (isNaN(num)) return '0%';
  
  // Format as percentage with 2 decimal places
  return num.toFixed(2) + '%';
};

// Compact number formatters used in tables (1K, 10K, 100K, 1M)
const formatNumberCompactShort = (value) => {
  const n = Number(value || 0);
  if (n >= 1000000000) return `${Math.round(n / 1000000000)}B`;
  if (n >= 1000000) return `${Math.round(n / 1000000)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
};

const formatCurrencyCompactShort = (value) => `₺${formatNumberCompactShort(value)}`;

// Cost with 1 decimal for K/M (e.g. 130.1K, 2.3M)
const formatCurrencyCompactShortDecimals = (value) => {
  const n = Number(value || 0);
  if (n >= 1000000000) return `₺${(n / 1000000000).toFixed(1)}B`;
  if (n >= 1000000) return `₺${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `₺${(n / 1000).toFixed(1)}K`;
  return `₺${Math.round(n).toLocaleString('tr-TR')}`;
};

// Currency formatter with two decimals (e.g., CPC)
const formatCurrencyFixed2 = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Suppress noisy backend warnings (e.g., prisma raw query relation does not exist)
const shouldSuppressBackendError = (msg) => {
  if (!msg) return false;
  const s = String(msg);
  return /prisma\.\$queryraw|42P01|relation .* does not exist/i.test(s);
};

// Special brand ID for dummy data
const SPECIAL_DUMMY_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

// Generate dummy Google Ads data
const generateDummyGoogleAdsData = () => {
  const today = new Date();
  
  // Generate daily labels for last 30 days
  const dailyLabels = [];
  const dailySpend = [];
  const dailyClicks = [];
  const dailyImpressions = [];
  const dailyConversions = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dailyLabels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    
    const baseSpend = 4500 + Math.random() * 3000;
    dailySpend.push(Math.round(baseSpend));
    dailyClicks.push(Math.round(baseSpend * (0.8 + Math.random() * 0.4)));
    dailyImpressions.push(Math.round(baseSpend * (15 + Math.random() * 10)));
    dailyConversions.push(Math.round(baseSpend * (0.02 + Math.random() * 0.02)));
  }
  
  const totalSpend = dailySpend.reduce((a, b) => a + b, 0);
  const totalClicks = dailyClicks.reduce((a, b) => a + b, 0);
  const totalImpressions = dailyImpressions.reduce((a, b) => a + b, 0);
  const totalConversions = dailyConversions.reduce((a, b) => a + b, 0);
  
  return {
    summary: {
      spend: totalSpend,
      impressions: totalImpressions,
      clicks: totalClicks,
      conversions: totalConversions,
      ctr: (totalClicks / totalImpressions * 100).toFixed(2),
      cpc: (totalSpend / totalClicks).toFixed(2),
      roas: 3.2,
      revenue: totalSpend * 3.2
    },
    campaigns: [
      {
        id: 'camp-search-1',
        name: 'TR - Search - Brand Keywords',
        type: 'AdvertisingChannelType.SEARCH',
        status: 'Active',
        budget: 25000,
        spend: 22500,
        impressions: 185000,
        clicks: 12500,
        ctr: 6.76,
        cpc: 1.80,
        conversions: 850,
        conversionRate: 6.80,
        revenue: 125000,
        roas: 5.56
      },
      {
        id: 'camp-search-2',
        name: 'TR - Search - Generic Keywords',
        type: 'AdvertisingChannelType.SEARCH',
        status: 'Active',
        budget: 35000,
        spend: 32800,
        impressions: 420000,
        clicks: 18500,
        ctr: 4.40,
        cpc: 1.77,
        conversions: 620,
        conversionRate: 3.35,
        revenue: 98000,
        roas: 2.99
      },
      {
        id: 'camp-pmax-1',
        name: 'TR - PMax - All Products',
        type: 'AdvertisingChannelType.PERFORMANCE_MAX',
        status: 'Active',
        budget: 45000,
        spend: 42500,
        impressions: 1250000,
        clicks: 28500,
        ctr: 2.28,
        cpc: 1.49,
        conversions: 1250,
        conversionRate: 4.39,
        revenue: 185000,
        roas: 4.35
      },
      {
        id: 'camp-pmax-2',
        name: 'TR - PMax - Best Sellers',
        type: 'AdvertisingChannelType.PERFORMANCE_MAX',
        status: 'Active',
        budget: 30000,
        spend: 28500,
        impressions: 850000,
        clicks: 19200,
        ctr: 2.26,
        cpc: 1.48,
        conversions: 920,
        conversionRate: 4.79,
        revenue: 142000,
        roas: 4.98
      },
      {
        id: 'camp-shopping-1',
        name: 'TR - Shopping - Standard',
        type: 'AdvertisingChannelType.SHOPPING',
        status: 'Active',
        budget: 20000,
        spend: 18500,
        impressions: 520000,
        clicks: 14200,
        ctr: 2.73,
        cpc: 1.30,
        conversions: 480,
        conversionRate: 3.38,
        revenue: 72000,
        roas: 3.89
      },
      {
        id: 'camp-display-1',
        name: 'TR - Display - Remarketing',
        type: 'AdvertisingChannelType.DISPLAY',
        status: 'Active',
        budget: 15000,
        spend: 12800,
        impressions: 2500000,
        clicks: 8500,
        ctr: 0.34,
        cpc: 1.51,
        conversions: 320,
        conversionRate: 3.76,
        revenue: 48000,
        roas: 3.75
      },
      {
        id: 'camp-video-1',
        name: 'TR - YouTube - Brand Awareness',
        type: 'AdvertisingChannelType.VIDEO',
        status: 'Active',
        budget: 12000,
        spend: 10500,
        impressions: 1800000,
        clicks: 4500,
        ctr: 0.25,
        cpc: 2.33,
        conversions: 85,
        conversionRate: 1.89,
        revenue: 15000,
        roas: 1.43
      },
      {
        id: 'camp-dsa-1',
        name: 'TR - DSA - All Pages',
        type: 'AdvertisingChannelType.SEARCH',
        status: 'Active',
        budget: 8000,
        spend: 7200,
        impressions: 95000,
        clicks: 5800,
        ctr: 6.11,
        cpc: 1.24,
        conversions: 180,
        conversionRate: 3.10,
        revenue: 28000,
        roas: 3.89
      }
    ],
    performance: {
      daily: {
        labels: dailyLabels,
        datasets: [
          {
            label: 'Spend',
            data: dailySpend,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true
          }
        ]
      },
      devices: {
        labels: ['Desktop', 'Mobile', 'Tablet'],
        datasets: [{
          label: 'Spend',
          data: [Math.round(totalSpend * 0.35), Math.round(totalSpend * 0.58), Math.round(totalSpend * 0.07)],
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(249, 115, 22, 0.8)'
          ]
        }]
      },
      geos: {
        labels: ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya'],
        datasets: [{
          label: 'Spend',
          data: [Math.round(totalSpend * 0.35), Math.round(totalSpend * 0.18), Math.round(totalSpend * 0.12), Math.round(totalSpend * 0.08), Math.round(totalSpend * 0.06)],
          backgroundColor: 'rgba(59, 130, 246, 0.8)'
        }]
      }
    }
  };
};

// Generate dummy combined campaign data
const generateDummyCombinedCampaignData = () => {
  return [
    { campaignType: 'search', cost: 62500, clicks: 36800, impressions: 700000, conversions: 1650, conversionValue: 251000, roas: 4.02 },
    { campaignType: 'performance_max', cost: 71000, clicks: 47700, impressions: 2100000, conversions: 2170, conversionValue: 327000, roas: 4.61 },
    { campaignType: 'shopping', cost: 18500, clicks: 14200, impressions: 520000, conversions: 480, conversionValue: 72000, roas: 3.89 },
    { campaignType: 'display', cost: 12800, clicks: 8500, impressions: 2500000, conversions: 320, conversionValue: 48000, roas: 3.75 },
    { campaignType: 'video', cost: 10500, clicks: 4500, impressions: 1800000, conversions: 85, conversionValue: 15000, roas: 1.43 }
  ];
};

// Generate dummy daily summary data
const generateDummyDailySummaryData = () => {
  const today = new Date();
  const data = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const baseCost = 4500 + Math.random() * 3000;
    data.push({
      date: date.toISOString().split('T')[0],
      cost: Math.round(baseCost),
      clicks: Math.round(baseCost * (0.8 + Math.random() * 0.4)),
      impressions: Math.round(baseCost * (15 + Math.random() * 10)),
      conversions: Math.round(baseCost * (0.02 + Math.random() * 0.02)),
      conversionValue: Math.round(baseCost * (2.5 + Math.random() * 1.5))
    });
  }
  
  return data;
};

// Generate dummy device performance data
const generateDummyDevicePerformanceData = () => {
  return [
    { device: 'MOBILE', cost: 98000, clicks: 65000, impressions: 4200000, conversions: 2800, conversionValue: 420000 },
    { device: 'DESKTOP', cost: 58000, clicks: 42000, impressions: 1800000, conversions: 1500, conversionValue: 285000 },
    { device: 'TABLET', cost: 12000, clicks: 8500, impressions: 450000, conversions: 280, conversionValue: 42000 }
  ];
};

// Generate dummy top keywords data
const generateDummyTopKeywordsData = () => {
  return [
    { keyword: 'kadın mont', matchType: 'PHRASE', cost: 8500, clicks: 4200, impressions: 85000, conversions: 185, conversionValue: 32000, ctr: 4.94, cpc: 2.02 },
    { keyword: 'erkek kazak', matchType: 'PHRASE', cost: 6800, clicks: 3500, impressions: 72000, conversions: 142, conversionValue: 24500, ctr: 4.86, cpc: 1.94 },
    { keyword: 'kış giyim', matchType: 'BROAD', cost: 5200, clicks: 2800, impressions: 95000, conversions: 98, conversionValue: 18000, ctr: 2.95, cpc: 1.86 },
    { keyword: 'indirimli kıyafet', matchType: 'PHRASE', cost: 4800, clicks: 3200, impressions: 68000, conversions: 125, conversionValue: 19500, ctr: 4.71, cpc: 1.50 },
    { keyword: 'online alışveriş', matchType: 'BROAD', cost: 4200, clicks: 2100, impressions: 120000, conversions: 65, conversionValue: 12000, ctr: 1.75, cpc: 2.00 },
    { keyword: 'marka giyim', matchType: 'EXACT', cost: 3800, clicks: 1900, impressions: 42000, conversions: 82, conversionValue: 15000, ctr: 4.52, cpc: 2.00 },
    { keyword: 'yeni sezon', matchType: 'PHRASE', cost: 3500, clicks: 1800, impressions: 55000, conversions: 72, conversionValue: 13500, ctr: 3.27, cpc: 1.94 },
    { keyword: 'elbise modelleri', matchType: 'PHRASE', cost: 3200, clicks: 1650, impressions: 48000, conversions: 58, conversionValue: 11000, ctr: 3.44, cpc: 1.94 },
    { keyword: 'çocuk giyim', matchType: 'PHRASE', cost: 2800, clicks: 1400, impressions: 38000, conversions: 52, conversionValue: 8500, ctr: 3.68, cpc: 2.00 },
    { keyword: 'spor giyim', matchType: 'BROAD', cost: 2500, clicks: 1200, impressions: 45000, conversions: 42, conversionValue: 7200, ctr: 2.67, cpc: 2.08 }
  ];
};

// Generate dummy monthly performance data
const generateDummyMonthlyPerformanceData = () => {
  const months = ['2024-09', '2024-10', '2024-11', '2024-12'];
  const campaignTypes = ['search', 'performance_max', 'shopping', 'display', 'video'];
  const data = [];
  
  months.forEach(month => {
    campaignTypes.forEach(type => {
      const baseCost = type === 'performance_max' ? 70000 : type === 'search' ? 60000 : type === 'shopping' ? 18000 : type === 'display' ? 12000 : 10000;
      const variance = 0.8 + Math.random() * 0.4;
      
      data.push({
        month,
        campaignType: type,
        cost: Math.round(baseCost * variance),
        clicks: Math.round(baseCost * variance * 0.8),
        impressions: Math.round(baseCost * variance * 25),
        conversions: Math.round(baseCost * variance * 0.03),
        conversionValue: Math.round(baseCost * variance * 3.5)
      });
    });
  });
  
  return data;
};

// Generate dummy impression share data
const generateDummyImpressionShareData = () => {
  return [
    { campaignName: 'TR - Search - Brand Keywords', campaignType: 'SEARCH', searchImpressionShare: 0.85, searchTopImpressionShare: 0.72, searchAbsoluteTopImpressionShare: 0.45, lostIsBudget: 0.05, lostIsRank: 0.10 },
    { campaignName: 'TR - Search - Generic Keywords', campaignType: 'SEARCH', searchImpressionShare: 0.62, searchTopImpressionShare: 0.48, searchAbsoluteTopImpressionShare: 0.28, lostIsBudget: 0.12, lostIsRank: 0.26 },
    { campaignName: 'TR - PMax - All Products', campaignType: 'PERFORMANCE_MAX', searchImpressionShare: 0.78, searchTopImpressionShare: 0.65, searchAbsoluteTopImpressionShare: 0.38, lostIsBudget: 0.08, lostIsRank: 0.14 },
    { campaignName: 'TR - PMax - Best Sellers', campaignType: 'PERFORMANCE_MAX', searchImpressionShare: 0.82, searchTopImpressionShare: 0.70, searchAbsoluteTopImpressionShare: 0.42, lostIsBudget: 0.06, lostIsRank: 0.12 },
    { campaignName: 'TR - Shopping - Standard', campaignType: 'SHOPPING', searchImpressionShare: 0.68, searchTopImpressionShare: 0.52, searchAbsoluteTopImpressionShare: 0.32, lostIsBudget: 0.10, lostIsRank: 0.22 },
    { campaignName: 'TR - DSA - All Pages', campaignType: 'SEARCH', searchImpressionShare: 0.55, searchTopImpressionShare: 0.42, searchAbsoluteTopImpressionShare: 0.25, lostIsBudget: 0.15, lostIsRank: 0.30 }
  ];
};

// Generate dummy conversion actions data
const generateDummyConversionActionsData = () => {
  return [
    { conversionActionName: 'Purchase', conversionActionCategory: 'PURCHASE', conversions: 2850, conversionValue: 485000, allConversions: 3200, allConversionValue: 520000 },
    { conversionActionName: 'Add to Cart', conversionActionCategory: 'ADD_TO_CART', conversions: 8500, conversionValue: 0, allConversions: 9200, allConversionValue: 0 },
    { conversionActionName: 'Begin Checkout', conversionActionCategory: 'BEGIN_CHECKOUT', conversions: 4200, conversionValue: 0, allConversions: 4800, allConversionValue: 0 },
    { conversionActionName: 'Page View', conversionActionCategory: 'PAGE_VIEW', conversions: 125000, conversionValue: 0, allConversions: 145000, allConversionValue: 0 },
    { conversionActionName: 'Sign Up', conversionActionCategory: 'SIGNUP', conversions: 1250, conversionValue: 0, allConversions: 1450, allConversionValue: 0 },
    { conversionActionName: 'Phone Call', conversionActionCategory: 'PHONE_CALL_LEAD', conversions: 320, conversionValue: 48000, allConversions: 380, allConversionValue: 57000 }
  ];
};

// Generate dummy unified ad platform data
const generateDummyUnifiedAdPlatformData = () => {
  const today = new Date();
  const data = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const baseCost = 5500 + Math.random() * 2500;
    data.push({
      date: date.toISOString().split('T')[0],
      googleAdsCost: Math.round(baseCost),
      googleAdsClicks: Math.round(baseCost * 0.75),
      googleAdsImpressions: Math.round(baseCost * 18),
      googleAdsConversions: Math.round(baseCost * 0.025),
      googleAdsConversionValue: Math.round(baseCost * 3.2),
      metaCost: Math.round(baseCost * 0.6),
      metaClicks: Math.round(baseCost * 0.5),
      metaImpressions: Math.round(baseCost * 25),
      metaConversions: Math.round(baseCost * 0.018),
      metaConversionValue: Math.round(baseCost * 2.4)
    });
  }
  
  return data;
};

// Generate dummy unified Google Ads + GA4 performance data
const generateDummyUnifiedGoogleAdsGa4Data = () => {
  const today = new Date();
  const data = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const baseCost = 5500 + Math.random() * 2500;
    data.push({
      date: date.toISOString().split('T')[0],
      cost: Math.round(baseCost),
      clicks: Math.round(baseCost * 0.75),
      impressions: Math.round(baseCost * 18),
      conversions: Math.round(baseCost * 0.025),
      conversionValue: Math.round(baseCost * 3.2),
      ga4Sessions: Math.round(baseCost * 0.85),
      ga4Users: Math.round(baseCost * 0.65),
      ga4Revenue: Math.round(baseCost * 3.5),
      ga4Transactions: Math.round(baseCost * 0.028)
    });
  }
  
  return data;
};

export default function GoogleAds() {
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState({
    performance: true,
    campaigns: true,
    keywords: true,
    unifiedAd: false,
    dailySummary: false,
    unifiedGaPerformance: false,
    devicePerformance: true,
    topKeywords: true,
    impressionShare: true,
    conversionActions: true,
  });
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("30days"); // Varsayılan olarak 30 gün
  const [lastFetchTime, setLastFetchTime] = useState(null);
  // Aktif metrik seçimi için yeni state
  const [activeMetric, setActiveMetric] = useState("Cost");
  const [data, setData] = useState({
    summary: {
      spend: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      ctr: 0,
      cpc: 0,
      roas: 0
    },
    campaigns: [],
    performance: {
      daily: {
        labels: [],
        datasets: [{
          label: 'Spend',
          data: [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true
        }]
      },
      devices: {
        labels: ['Desktop', 'Mobile', 'Tablet'],
        datasets: [{
          label: 'Spend',
          data: [0, 0, 0],
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(249, 115, 22, 0.8)'
          ]
        }]
      },
      geos: {
        labels: [],
        datasets: [{
          label: 'Spend',
          data: [],
          backgroundColor: 'rgba(59, 130, 246, 0.8)'
        }]
      }
    }
  });

  // Add new states for campaign type breakdown
  const [selectedCampaignType, setSelectedCampaignType] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showAdGroups, setShowAdGroups] = useState(false);
  // No fake default — start at null. UI shows an inline input when no target is set in DB.
  const [targetBudget, setTargetBudget] = useState(null);
  const [isEditingBudget, setIsEditingBudget] = useState(false); // Bütçe düzenleme durumu
  const [savingTargetBudget, setSavingTargetBudget] = useState(false);

  // Load spend_target from monthly_targets_v2 for the current month/year (Google Ads channel)
  useEffect(() => {
    let cancelled = false;
    const loadMonthlyTarget = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth() + 1;
        const res = await fetch(
          `https://api.glowytics.com/api/monthly-targets?year=${y}&month=${m}&channel_key=GOOGLE_ADS`,
          { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
        );
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const rows = Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
        if (rows.length > 0) {
          const row = rows.find(r => !r.account_id) || rows[0];
          if (row?.spend_target != null && !cancelled) {
            setTargetBudget(Number(row.spend_target));
          }
        }
      } catch (e) {
        console.warn('[GoogleAds] failed to load monthly target:', e.message);
      }
    };
    loadMonthlyTarget();
    // Re-fetch when brand changes mid-session (storage event from other tabs +
    // a custom 'brandChanged' event the app may dispatch on switch).
    const handler = () => loadMonthlyTarget();
    window.addEventListener('storage', handler);
    window.addEventListener('brandChanged', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handler);
      window.removeEventListener('brandChanged', handler);
    };
  }, []);

  // Persist target budget to monthly_targets_v2 DB (current month, GOOGLE_ADS channel)
  const saveMonthlyTarget = async (numValue) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { toast.error('No brand selected'); return; }
      setSavingTargetBudget(true);
      const now = new Date();
      const res = await fetch('https://api.glowytics.com/api/monthly-targets', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          channel_key: 'GOOGLE_ADS',
          spend_target: numValue,
          revenue_source: 'ga4'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setTargetBudget(numValue);
      toast.success('Monthly target saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save target');
    } finally {
      setSavingTargetBudget(false);
    }
  };

  // Yeni state ekleyelim
  const [monthlyPerformanceApiData, setMonthlyPerformanceApiData] = useState([]);
  const [combinedCampaignData, setCombinedCampaignData] = useState([]);
  
  // Yeni state ekleyelim
  const [dailySummaryData, setDailySummaryData] = useState([]);
  const [devicePerformanceData, setDevicePerformanceData] = useState([]);
  const [activeDeviceMetric, setActiveDeviceMetric] = useState('cost');
  const [topKeywordsData, setTopKeywordsData] = useState([]);
  
  // Kampanya tipi verisi için yeni state ekliyorum
  const [campaignTypeData, setCampaignTypeData] = useState(null);

  // impression share için state
  const [impressionShareData, setImpressionShareData] = useState([]);
  const [conversionActionsData, setConversionActionsData] = useState([]);

  // unified_googleads_ga4_performance için state
  const [unifiedGaData, setUnifiedGaData] = useState([]);
  const [allTimeUnifiedGaData, setAllTimeUnifiedGaData] = useState([]);
  const [gaAccountFilter, setGaAccountFilter] = useState('all');

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Custom date & all-time destek state'leri (Date objects)
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  // Expand/collapse state for campaign type rows
  const [expandedMonthlyTypes, setExpandedMonthlyTypes] = useState({});
  const [expanded14dTypes, setExpanded14dTypes] = useState({});
  const [expandedWeeklyTypes, setExpandedWeeklyTypes] = useState({});

  /** Yataş grubu: kampanya adında "performance"/"performans" veya "branding" */
  const [yatasCampaignFilter, setYatasCampaignFilter] = useState('all');
  const combinedCampaignRawRef = useRef([]);

  // Local YYYY-MM-DD formatter (avoid timezone shift)
  const formatLocalDateYMD = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Globally suppress noisy prisma 42P01 toasts while this page is mounted
  useEffect(() => {
    const origError = toast.error;
    toast.error = (arg, ...rest) => {
      try {
        const msg = typeof arg === 'string' ? arg : (arg && arg.message) ? arg.message : String(arg);
        if (shouldSuppressBackendError(msg)) return;
      } catch (_) {}
      return origError(arg, ...rest);
    };
    return () => {
      toast.error = origError;
    };
  }, []);

  // Tarih parametrelerini oluşturan yardımcı fonksiyon
  const buildQueryParams = useCallback((overrideRange) => {
    const range = overrideRange || dateRange;
    const accountParam = gaAccountFilter && gaAccountFilter !== 'all' ? `&accountId=${gaAccountFilter}` : '';
    if (range === 'custom' && customStartDate && customEndDate) {
      return `?dateRange=custom&startDate=${formatLocalDateYMD(customStartDate)}&endDate=${formatLocalDateYMD(customEndDate)}${accountParam}`;
    }
    if (range === 'all_time') {
      return accountParam ? `?dateRange=all_time&accountId=${gaAccountFilter}` : '?dateRange=all_time';
    }
    return `?dateRange=${range}${accountParam}`;
  }, [dateRange, customStartDate, customEndDate, gaAccountFilter]);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const showYatasCampaignFilter = brandId && YATAS_GROUP_BRAND_IDS.has(brandId);

  // Google Ads account filter
  const gaAccountNameMap = useMemo(() => {
    const map = {};
    unifiedGaData.forEach(r => {
      if (r.account_id && r.account_name) map[r.account_id] = r.account_name;
    });
    return map;
  }, [unifiedGaData]);

  const gaAccountOptions = useMemo(() => {
    const ids = new Set();
    unifiedGaData.forEach(r => { if (r.account_id) ids.add(r.account_id); });
    return Array.from(ids).sort();
  }, [unifiedGaData]);

  const accountFilteredGaData = useMemo(() => {
    if (gaAccountFilter === 'all') return unifiedGaData;
    return unifiedGaData.filter(r => r.account_id === gaAccountFilter);
  }, [unifiedGaData, gaAccountFilter]);

  const filteredUnifiedGaData = useMemo(() => {
    if (!accountFilteredGaData || accountFilteredGaData.length === 0) return [];
    if (!showYatasCampaignFilter || yatasCampaignFilter === 'all') return accountFilteredGaData;
    return accountFilteredGaData.filter((row) =>
      matchesYatasCampaignNameFilter(row.campaign_name || row.campaignName, yatasCampaignFilter)
    );
  }, [accountFilteredGaData, yatasCampaignFilter, showYatasCampaignFilter]);

  const campaignPerformanceData = useMemo(() => {
    if (!filteredUnifiedGaData || filteredUnifiedGaData.length === 0) {
      return [];
    }

    const groupedData = {};
    const campaignsByType = {};

    filteredUnifiedGaData.forEach(item => {
      const campaignType = item.campaign_type || 'Unknown';

      if (!campaignsByType[campaignType]) {
        campaignsByType[campaignType] = new Set();
      }
      if (item.campaign_id) {
        campaignsByType[campaignType].add(item.campaign_id);
      }

      if (!groupedData[campaignType]) {
        groupedData[campaignType] = {
          campaign_type: campaignType,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversion_value: 0,
          ga4_sessions: 0,
          ga4_purchases: 0,
          ga4_total_revenue: 0,
        };
      }

      groupedData[campaignType].impressions += Number(item.impressions || 0);
      groupedData[campaignType].clicks += Number(item.clicks || 0);
      groupedData[campaignType].cost += Number(item.cost || 0);
      groupedData[campaignType].conversions += Number(item.conversions || 0);
      groupedData[campaignType].conversion_value += Number(item.conversion_value || 0);
      groupedData[campaignType].ga4_sessions += Number(item.ga4_sessions || 0);
      groupedData[campaignType].ga4_purchases += Number(item.ga4_purchases || 0);
      groupedData[campaignType].ga4_total_revenue += Number(item.ga4_total_revenue || 0);
    });

    const finalData = Object.keys(groupedData).map(type => {
      const item = groupedData[type];
      
      item.cpc = item.clicks > 0 ? item.cost / item.clicks : 0;
      item.cps = item.ga4_sessions > 0 ? item.cost / item.ga4_sessions : 0;
      item.roas = item.cost > 0 ? item.conversion_value / item.cost : 0;
      item.campaignCount = campaignsByType[type] ? campaignsByType[type].size : 0;
      
      return item;
    });

    finalData.sort((a, b) => b.cost - a.cost);

    if (finalData.length === 0) {
      return [];
    }

    const totalCampaignCount = Object.values(campaignsByType).reduce((total, campaigns) => total + campaigns.size, 0);

    const total = finalData.reduce((acc, item) => {
      acc.impressions += item.impressions;
      acc.clicks += item.clicks;
      acc.cost += item.cost;
      acc.conversions += item.conversions;
      acc.conversion_value += item.conversion_value;
      acc.ga4_sessions += item.ga4_sessions;
      acc.ga4_purchases += item.ga4_purchases;
      acc.ga4_total_revenue += item.ga4_total_revenue;
      return acc;
    }, {
      campaign_type: 'TOTAL',
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversion_value: 0,
      ga4_sessions: 0,
      ga4_purchases: 0,
      ga4_total_revenue: 0,
      campaignCount: totalCampaignCount,
    });

    total.cpc = total.clicks > 0 ? total.cost / total.clicks : 0;
    total.cps = total.ga4_sessions > 0 ? total.cost / total.ga4_sessions : 0;
    total.roas = total.cost > 0 ? total.conversion_value / total.cost : 0;

    finalData.push(total);

    return finalData;

  }, [filteredUnifiedGaData]);

  const dailyUnifiedPerformance = useMemo(() => {
    if (!filteredUnifiedGaData || filteredUnifiedGaData.length === 0) {
      return { daily: [], totals: { cost: 0, conversion_value: 0, roas: 0, ga4_total_revenue: 0 } };
    }

    const dailyData = {};
    filteredUnifiedGaData.forEach(item => {
      const date = item.date.split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          cost: 0,
          clicks: 0,
          conversion_value: 0,
          ga4_total_revenue: 0,
        };
      }
      dailyData[date].cost += Number(item.cost || 0);
      dailyData[date].clicks += Number(item.clicks || 0);
      dailyData[date].conversion_value += Number(item.conversion_value || 0);
      dailyData[date].ga4_total_revenue += Number(item.ga4_total_revenue || 0);
    });

    const dailyArray = Object.values(dailyData).sort((a, b) => new Date(b.date) - new Date(a.date));

    const totals = dailyArray.reduce((acc, day) => {
        acc.cost += day.cost;
        acc.clicks += day.clicks;
        acc.conversion_value += day.conversion_value;
        acc.ga4_total_revenue += day.ga4_total_revenue;
        return acc;
    }, { cost: 0, clicks: 0, conversion_value: 0, ga4_total_revenue: 0 });
    
    totals.roas = totals.cost > 0 ? totals.conversion_value / totals.cost : 0;
    
    return { 
        daily: dailyArray, 
        totals: {
            cost: totals.cost || 0,
            clicks: totals.clicks || 0,
            conversion_value: totals.conversion_value || 0,
            ga4_total_revenue: totals.ga4_total_revenue || 0,
            roas: totals.roas || 0
        }
    };
  }, [filteredUnifiedGaData]);

  const chartData = useMemo(() => {
    if (!dailyUnifiedPerformance || !dailyUnifiedPerformance.daily) {
        return { labels: [], datasets: [] };
    }

    const dataMap = new Map(dailyUnifiedPerformance.daily.map(item => [item.date, item]));
    
    const getDatesForRange = () => {
        const dates = [];
        let endDate = new Date();
        let startDate = new Date();

        switch (dateRange) {
            case 'today':
                startDate = new Date();
                break;
            case 'yesterday':
                startDate.setDate(endDate.getDate() - 1);
                endDate.setDate(endDate.getDate() - 1);
                break;
            case '7days':
                startDate.setDate(endDate.getDate() - 6);
                break;
            case 'this_month':
                startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
                break;
            case 'custom': {
                if (customStartDate && customEndDate) {
                    const s = customStartDate;
                    const e = customEndDate;
                    if (!isNaN(s) && !isNaN(e) && s <= e) {
                        startDate = s;
                        endDate = e;
                        break;
                    }
                }
                startDate.setDate(endDate.getDate() - 29);
                break;
            }
            case 'all_time': {
                if (dailyUnifiedPerformance.daily.length > 0) {
                    const allDates = dailyUnifiedPerformance.daily.map(d => new Date(d.date));
                    const minDate = new Date(Math.min(...allDates));
                    const maxDate = new Date(Math.max(...allDates));
                    if (!isNaN(minDate) && !isNaN(maxDate) && minDate <= maxDate) {
                        startDate = minDate;
                        endDate = maxDate;
                        break;
                    }
                }
                startDate.setDate(endDate.getDate() - 29);
                break;
            }
            case '30days':
            default:
                startDate.setDate(endDate.getDate() - 29);
                break;
        }

        for (let dt = new Date(startDate); dt <= endDate; dt.setDate(dt.getDate() + 1)) {
            dates.push(new Date(dt));
        }
        return dates;
    };

    const allDatesInRange = getDatesForRange();
    
    const completeLabels = allDatesInRange.map(date => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
    
    const completeCostData = allDatesInRange.map(date => {
        const dateStr = formatLocalDateYMD(date);
        return dataMap.get(dateStr)?.cost || 0;
    });

    const completeRevenueData = allDatesInRange.map(date => {
        const dateStr = formatLocalDateYMD(date);
        return dataMap.get(dateStr)?.ga4_total_revenue || 0;
    });

    return {
        labels: completeLabels,
        datasets: [
            {
                label: 'Actual Cost',
                data: completeCostData,
                yAxisID: 'y1',
                borderColor: '#3b82f6',
                backgroundColor: function(context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'transparent';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(59, 130, 246, 0.35)');
                    gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
                    return gradient;
                },
                fill: true,
                tension: 0.35,
                borderWidth: 2.5,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: '#3b82f6',
                pointHoverBorderWidth: 2,
                spanGaps: true
            },
            {
                label: 'Revenue',
                data: completeRevenueData,
                yAxisID: 'y',
                borderColor: '#10b981',
                backgroundColor: function(context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'transparent';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.35)');
                    gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                    return gradient;
                },
                fill: true,
                tension: 0.35,
                borderWidth: 2.5,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: '#10b981',
                pointHoverBorderWidth: 2,
                spanGaps: true
            }
        ]
    };
  }, [dailyUnifiedPerformance, dateRange, customStartDate, customEndDate]);

  const topCampaignsData = useMemo(() => {
    if (!filteredUnifiedGaData || filteredUnifiedGaData.length === 0) {
      return [];
    }

    const campaignSummary = {};

    filteredUnifiedGaData.forEach(item => {
      const campaignId = item.campaign_id;
      if (!campaignSummary[campaignId]) {
        campaignSummary[campaignId] = {
          id: campaignId,
          name: item.campaign_name,
          cost: 0,
          clicks: 0,
          impressions: 0,
          conversions: 0,
          conversion_value: 0,
          ga4_total_revenue: 0,
        };
      }

      const campaign = campaignSummary[campaignId];
      campaign.cost += Number(item.cost || 0);
      campaign.clicks += Number(item.clicks || 0);
      campaign.impressions += Number(item.impressions || 0);
      campaign.conversions += Number(item.conversions || 0);
      campaign.conversion_value += Number(item.conversion_value || 0);
      campaign.ga4_total_revenue += Number(item.ga4_total_revenue || 0);
    });
    
    const campaignsArray = Object.values(campaignSummary).map(c => ({
      ...c,
      roas: c.cost > 0 ? c.conversion_value / c.cost : 0,
      cpc: c.clicks > 0 ? c.cost / c.clicks : 0,
    }));

    // Sort by cost and take top 10
    return campaignsArray.sort((a, b) => b.cost - a.cost).slice(0, 10);

  }, [filteredUnifiedGaData]);

  const monthlyCampaignPerformanceData = useMemo(() => {
    if (!monthlyPerformanceApiData || monthlyPerformanceApiData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const dataByMonth = {};
    monthlyPerformanceApiData.forEach(item => {
        const monthKey = new Date(item.month).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
        if (!dataByMonth[monthKey]) {
            dataByMonth[monthKey] = {};
        }
        dataByMonth[monthKey][item.campaignType] = item;
    });

    const sortedMonthKeys = Object.keys(dataByMonth).sort((a, b) => new Date(a) - new Date(b));

    const labels = sortedMonthKeys;

    const campaignTypesPresent = [...new Set(monthlyPerformanceApiData.map(i => i.campaignType))];
    
    const datasets = campaignTypesPresent.map(campaignType => {
        const typeInfo = campaignTypes[campaignType] || { color: 'gray', label: campaignType };
        const colorMap = { 'blue': 'rgb(59, 130, 246)', 'purple': 'rgb(139, 92, 246)', 'green': 'rgb(16, 185, 129)', 'pink': 'rgb(236, 72, 153)', 'yellow': 'rgb(234, 179, 8)', 'red': 'rgb(239, 68, 68)', 'indigo': 'rgb(79, 70, 229)', 'teal': 'rgb(20, 184, 166)', 'amber': 'rgb(245, 158, 11)', 'cyan': 'rgb(6, 182, 212)', 'orange': 'rgb(249, 115, 22)', 'gray': 'rgb(107, 114, 128)' };
        const color = colorMap[typeInfo.color] || colorMap.gray;

        const data = sortedMonthKeys.map(monthKey => {
            const monthDataForType = dataByMonth[monthKey][campaignType];
            if (!monthDataForType) return 0;
            switch(activeMetric) {
                case 'Clicks': return monthDataForType.clicks || 0;
                case 'Impressions': return monthDataForType.impressions || 0;
                case 'Conversions': return monthDataForType.conversions || 0;
                case 'ROAS': return monthDataForType.roas || 0;
                case 'Cost':
                default:
                    return monthDataForType.cost || 0;
            }
        });

        return {
            label: typeInfo.label || campaignType,
            data: data,
            borderColor: color,
            backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
            tension: 0.3,
            borderWidth: 2,
            pointBackgroundColor: color,
            pointRadius: 2,
            fill: false
        };
    });

    return { labels, datasets };
}, [monthlyPerformanceApiData, activeMetric]);

  const pivotTableData = useMemo(() => {
    if (!monthlyPerformanceApiData || monthlyPerformanceApiData.length === 0) {
      return { headers: [], rows: [], totals: [] };
    }

    const headers = ['Campaign Type', ...[...new Set(monthlyPerformanceApiData.map(item => new Date(item.month).toLocaleString('en-GB', { month: 'short', year: 'numeric' })))].sort((a, b) => new Date(a) - new Date(b))];
    const campaignTypes = [...new Set(monthlyPerformanceApiData.map(item => item.campaignType))];

    const dataMap = monthlyPerformanceApiData.reduce((acc, item) => {
      const monthKey = new Date(item.month).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
      const typeKey = item.campaignType;
      if (!acc[typeKey]) {
        acc[typeKey] = {};
      }
      acc[typeKey][monthKey] = item.cost || 0;
      return acc;
    }, {});
  
    const rows = campaignTypes.map(type => {
      const rowData = {
        campaignType: type,
        costs: headers.slice(1).map(month => dataMap[type]?.[month] || 0)
      };
      rowData.total = rowData.costs.reduce((sum, cost) => sum + cost, 0);
      return rowData;
    });

    const totals = headers.slice(1).map(month => {
      return rows.reduce((sum, row) => sum + (row.costs[headers.slice(1).indexOf(month)] || 0), 0);
    });
    
    totals.push(totals.reduce((sum, total) => sum + total, 0));
  
    headers.push('Total');
  
    return { headers, rows, totals };
  }, [monthlyPerformanceApiData]);

  const performancePivotTableData = useMemo(() => {
    if (!monthlyPerformanceApiData || monthlyPerformanceApiData.length === 0) {
      return { headers: [], rows: [], totals: [] };
    }

    // Build month headers first (excluding leading label and trailing Total for now)
    const monthKeys = [...new Set(
      monthlyPerformanceApiData.map(item => new Date(item.month).toLocaleString('en-GB', { month: 'short', year: 'numeric' }))
    )].sort((a, b) => new Date(a) - new Date(b));
    const headers = ['Campaign Type', ...monthKeys];
    const campaignTypesPresent = [...new Set(monthlyPerformanceApiData.map(item => item.campaignType))];

    // Base aggregator: keep sums to support ratio metrics correctly
    const baseAgg = monthlyPerformanceApiData.reduce((acc, item) => {
      const monthKey = new Date(item.month).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
      const typeKey = item.campaignType;
      if (!acc[typeKey]) acc[typeKey] = {};
      if (!acc[typeKey][monthKey]) acc[typeKey][monthKey] = { cost: 0, clicks: 0, impressions: 0, conversions: 0, revenue: 0, ga4_sessions: 0 };
      acc[typeKey][monthKey].cost += Number(item.cost || 0);
      acc[typeKey][monthKey].clicks += Number(item.clicks || 0);
      acc[typeKey][monthKey].impressions += Number(item.impressions || 0);
      acc[typeKey][monthKey].conversions += Number(item.conversions || 0);
      acc[typeKey][monthKey].revenue += Number(item.conversionValue || 0);
      return acc;
    }, {});

    // Enrich with GA4 sessions from unified GA data (type + month)
    if (Array.isArray(allTimeUnifiedGaData) && allTimeUnifiedGaData.length > 0) {
      allTimeUnifiedGaData.forEach(item => {
        const dt = item.date ? new Date(item.date) : null;
        if (!dt) return;
        const monthKey = dt.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
        if (!monthKeys.includes(monthKey)) return;
        const typeKey = item.campaign_type || item.campaignType || 'Unknown';
        if (!baseAgg[typeKey]) baseAgg[typeKey] = {};
        if (!baseAgg[typeKey][monthKey]) baseAgg[typeKey][monthKey] = { cost: 0, clicks: 0, impressions: 0, conversions: 0, revenue: 0, ga4_sessions: 0 };
        baseAgg[typeKey][monthKey].ga4_sessions += Number(item.ga4_sessions || item.sessions || 0);
      });
    }

    const rows = campaignTypesPresent.map(type => {
      // Build values depending on active metric
      const values = monthKeys.map(month => {
        const cell = baseAgg[type]?.[month] || { cost: 0, clicks: 0, impressions: 0, conversions: 0, revenue: 0, ga4_sessions: 0 };
        switch (activeMetric) {
          case 'Clicks': return cell.clicks;
          case 'Impressions': return cell.impressions;
          case 'Conversions': return cell.conversions;
          case 'Revenue': return cell.revenue;
          case 'Sessions': return cell.ga4_sessions;
          case 'CPS': return cell.ga4_sessions > 0 ? (cell.cost / cell.ga4_sessions) : 0; // CPS = cost per session
          case 'CPC': return cell.clicks > 0 ? (cell.cost / cell.clicks) : 0;
          case 'ROAS': return cell.cost > 0 ? (cell.revenue / cell.cost) : 0;
          case 'Cost+Revenue': return { cost: cell.cost, revenue: cell.revenue };
          case 'Cost':
          default: return cell.cost;
        }
      });

      // Totals: ratios aggregated correctly
      let total;
      if (activeMetric === 'ROAS') {
        let sumCost = 0, sumRevenue = 0;
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
        total = sumCost > 0 ? (sumRevenue / sumCost) : 0;
      } else if (activeMetric === 'CPS') {
        let sumCost = 0, sumSessions = 0;
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumSessions += (c.ga4_sessions || 0); } });
        total = sumSessions > 0 ? (sumCost / sumSessions) : 0;
      } else if (activeMetric === 'CPC') {
        let sumCost = 0, sumClicks = 0;
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumClicks += c.clicks; } });
        total = sumClicks > 0 ? (sumCost / sumClicks) : 0;
      } else if (activeMetric === 'Cost+Revenue') {
        let sumCost = 0, sumRevenue = 0;
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
        total = { cost: sumCost, revenue: sumRevenue };
      } else {
        total = values.reduce((s, v) => s + (Number(v) || 0), 0);
      }

      return { campaignType: type, values, total };
    });

    // Column totals
    const totals = monthKeys.map(month => {
      if (activeMetric === 'ROAS') {
        let sumCost = 0, sumRevenue = 0;
        campaignTypesPresent.forEach(type => { const c = baseAgg[type]?.[month]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
        return sumCost > 0 ? (sumRevenue / sumCost) : 0;
      }
      if (activeMetric === 'CPS') {
        let sumCost = 0, sumSessions = 0;
        campaignTypesPresent.forEach(type => { const c = baseAgg[type]?.[month]; if (c) { sumCost += c.cost; sumSessions += (c.ga4_sessions || 0); } });
        return sumSessions > 0 ? (sumCost / sumSessions) : 0;
      }
      if (activeMetric === 'CPC') {
        let sumCost = 0, sumClicks = 0;
        campaignTypesPresent.forEach(type => { const c = baseAgg[type]?.[month]; if (c) { sumCost += c.cost; sumClicks += c.clicks; } });
        return sumClicks > 0 ? (sumCost / sumClicks) : 0;
      }
      if (activeMetric === 'Cost+Revenue') {
        let sumCost = 0, sumRevenue = 0;
        campaignTypesPresent.forEach(type => { const c = baseAgg[type]?.[month]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
        return { cost: sumCost, revenue: sumRevenue };
      }
      // Non-ratio metrics: sum
      return rows.reduce((sum, row) => sum + (row.values[monthKeys.indexOf(month)] || 0), 0);
    });
    
    // Grand total column
    if (activeMetric === 'ROAS') {
      let sumCost = 0, sumRevenue = 0;
      campaignTypesPresent.forEach(type => {
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
      });
      totals.push(sumCost > 0 ? (sumRevenue / sumCost) : 0);
    } else if (activeMetric === 'CPC') {
      let sumCost = 0, sumClicks = 0;
      campaignTypesPresent.forEach(type => {
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumClicks += c.clicks; } });
      });
      totals.push(sumClicks > 0 ? (sumCost / sumClicks) : 0);
    } else if (activeMetric === 'CPS') {
      let sumCost = 0, sumClicks = 0;
      campaignTypesPresent.forEach(type => {
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumClicks += (c.clicks || 0); } });
      });
      totals.push(sumClicks > 0 ? (sumCost / sumClicks) : 0);
    } else if (activeMetric === 'Cost+Revenue') {
      let sumCost = 0, sumRevenue = 0;
      campaignTypesPresent.forEach(type => {
        monthKeys.forEach(mk => { const c = baseAgg[type]?.[mk]; if (c) { sumCost += c.cost; sumRevenue += c.revenue; } });
      });
      totals.push({ cost: sumCost, revenue: sumRevenue });
    } else {
      totals.push(totals.reduce((sum, total) => sum + total, 0));
    }

    headers.push('Total');

    // Attach children aggregated from all-time unified GA data, grouped by month
    if (Array.isArray(allTimeUnifiedGaData) && allTimeUnifiedGaData.length > 0) {
      // Helper to extract base metrics
      const getVals = (item) => ({
        cost: Number(item.cost || 0),
        revenue: Number(item.conversion_value || 0),
        clicks: Number(item.clicks || 0),
        impressions: Number(item.impressions || 0),
        conversions: Number(item.conversions || 0),
        ga4_sessions: Number(item.ga4_sessions || item.sessions || 0)
      });

      // Build children map: type -> campaignName -> monthKey -> aggregated metrics
      const childrenByTypeNameMonth = {};
      allTimeUnifiedGaData.forEach(item => {
        const type = item.campaign_type || 'Unknown';
        const name = item.campaign_name || item.campaignName || 'Unnamed';
        const dt = item.date ? new Date(item.date) : null;
        if (!dt) return;
        const monthKey = dt.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
        // Only keep months that are in our monthKeys window
        if (!monthKeys.includes(monthKey)) return;
        if (!childrenByTypeNameMonth[type]) childrenByTypeNameMonth[type] = {};
        if (!childrenByTypeNameMonth[type][name]) childrenByTypeNameMonth[type][name] = {};
        if (!childrenByTypeNameMonth[type][name][monthKey]) {
          childrenByTypeNameMonth[type][name][monthKey] = { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, ga4_sessions: 0 };
        }
        const v = getVals(item);
        const cell = childrenByTypeNameMonth[type][name][monthKey];
        cell.cost += v.cost;
        cell.revenue += v.revenue;
        cell.clicks += v.clicks;
        cell.impressions += v.impressions;
        cell.conversions += v.conversions;
        cell.ga4_sessions += v.ga4_sessions;
      });

      // Attach to each parent row
      rows.forEach(r => {
        const mapByName = childrenByTypeNameMonth[r.campaignType] || {};
        const children = Object.keys(mapByName).map(name => {
          // Build values per month according to activeMetric
          const perMonth = monthKeys.map(mk => {
            const cell = mapByName[name][mk] || { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, ga4_sessions: 0 };
            switch (activeMetric) {
              case 'Clicks': return cell.clicks;
              case 'Impressions': return cell.impressions;
              case 'Conversions': return cell.conversions;
              case 'Revenue': return cell.revenue;
              case 'Cost+Revenue': return { cost: cell.cost, revenue: cell.revenue };
              case 'CPS': return cell.clicks > 0 ? (cell.cost / cell.clicks) : 0;
              case 'CPC': return cell.clicks > 0 ? (cell.cost / cell.clicks) : 0;
              case 'ROAS': return cell.cost > 0 ? (cell.revenue / cell.cost) : 0;
              case 'Cost':
              default: return cell.cost;
            }
          });

          // Compute total; for ratio metrics compute aggregated ratio
          let total;
          if (activeMetric === 'ROAS') {
            // aggregate: sum(revenue)/sum(cost)
            let sumC = 0, sumR = 0;
            monthKeys.forEach(mk => { const c = mapByName[name][mk]; if (c) { sumC += c.cost; sumR += c.revenue; } });
            total = sumC > 0 ? (sumR / sumC) : 0;
          } else if (activeMetric === 'CPC') {
            // aggregate: sum(cost)/sum(clicks)
            let sumC = 0, sumK = 0;
            monthKeys.forEach(mk => { const c = mapByName[name][mk]; if (c) { sumC += c.cost; sumK += c.clicks; } });
            total = sumK > 0 ? (sumC / sumK) : 0;
          } else if (activeMetric === 'CPS') {
            // aggregate: sum(cost)/sum(clicks)
            let sumC = 0, sumK = 0;
            monthKeys.forEach(mk => { const c = mapByName[name][mk]; if (c) { sumC += c.cost; sumK += c.clicks; } });
            total = sumK > 0 ? (sumC / sumK) : 0;
          } else if (activeMetric === 'Cost+Revenue') {
            let sumC = 0, sumR = 0;
            monthKeys.forEach(mk => { const c = mapByName[name][mk]; if (c) { sumC += c.cost; sumR += c.revenue; } });
            total = { cost: sumC, revenue: sumR };
          } else {
            total = perMonth.reduce((s, v) => s + (Number(v) || 0), 0);
          }

          return { name, values: perMonth, total };
        }).sort((a, b) => b.total - a.total);
        r.children = children;
      });
    }

    return { headers, rows, totals };
  }, [monthlyPerformanceApiData, activeMetric, allTimeUnifiedGaData]);

  // Last 30 days daily (fixed, independent) pivot by campaign type
  const last30PivotTableData = useMemo(() => {
    if (!allTimeUnifiedGaData || allTimeUnifiedGaData.length === 0) {
      return { headers: [], rows: [], totals: [] };
    }

    // Fixed window: last 10 days ending yesterday
    const end = new Date();
    end.setDate(end.getDate() - 1); // yesterday
    const start = new Date(end);
    start.setDate(end.getDate() - 9); // 10-day range total

    // Build ordered date keys
    const dateList = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dateList.push(new Date(d));
    }
    const dayKeys = dateList.map(d => formatLocalDateYMD(d));

    // Prepare structures
    const byTypeDate = {};

    // Helper extractors
    const getVals = (item) => ({
      cost: Number(item.cost || 0),
      revenue: Number(item.conversion_value || 0),
      clicks: Number(item.clicks || 0),
      impressions: Number(item.impressions || 0),
      conversions: Number(item.conversions || 0),
      sessions: Number(item.ga4_sessions || item.sessions || 0)
    });

    allTimeUnifiedGaData.forEach(item => {
      const dt = item.date ? new Date(item.date) : null;
      if (!dt || dt < start || dt > end) return;
      const key = typeof item.date === 'string' ? item.date.split('T')[0] : formatLocalDateYMD(dt);
      const type = item.campaign_type || 'Unknown';
      if (!byTypeDate[type]) byTypeDate[type] = {};
      if (!byTypeDate[type][key]) byTypeDate[type][key] = { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, sessions: 0 };
      const v = getVals(item);
      byTypeDate[type][key].cost += v.cost;
      byTypeDate[type][key].revenue += v.revenue;
      byTypeDate[type][key].clicks += v.clicks;
      byTypeDate[type][key].impressions += v.impressions;
      byTypeDate[type][key].conversions += v.conversions;
      byTypeDate[type][key].sessions += v.sessions;
    });

    const rows = Object.keys(byTypeDate).map(type => {
      let sumCost = 0, sumRevenue = 0, sumSessions = 0, total = 0;
      const values = dayKeys.map(k => {
        const cell = byTypeDate[type][k] || { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, sessions: 0 };
        sumCost += cell.cost; sumRevenue += cell.revenue; sumSessions += cell.sessions;
        let val = 0;
        switch (activeMetric) {
          case 'Clicks': val = cell.clicks; break;
          case 'Impressions': val = cell.impressions; break;
          case 'Conversions': val = cell.conversions; break;
          case 'Revenue': val = cell.revenue; break;
          case 'Cost+Revenue': val = { cost: cell.cost, revenue: cell.revenue }; break;
          case 'CPS': val = cell.sessions > 0 ? cell.cost / cell.sessions : 0; break;
          case 'CPC': val = cell.clicks > 0 ? cell.cost / cell.clicks : 0; break;
          case 'ROAS': val = cell.cost > 0 ? cell.revenue / cell.cost : 0; break;
          case 'Cost':
          default: val = cell.cost; break;
        }
        total += typeof val === 'object' ? 0 : val;
        return val;
      });
      let totalVal;
      if (activeMetric === 'ROAS') {
        totalVal = sumCost > 0 ? sumRevenue / sumCost : 0;
      } else if (activeMetric === 'CPC') {
        const totClicks = dayKeys.reduce((s,k)=> s + ((byTypeDate[type][k]||{}).clicks || 0), 0);
        totalVal = totClicks>0 ? (sumCost / totClicks) : 0;
      } else if (activeMetric === 'CPS') {
        const totSessions = dayKeys.reduce((s,k)=> s + ((byTypeDate[type][k]||{}).sessions || 0), 0);
        totalVal = totSessions>0 ? (sumCost / totSessions) : 0;
      } else if (activeMetric === 'Cost+Revenue') {
        totalVal = { cost: sumCost, revenue: sumRevenue };
      } else {
        totalVal = total;
      }
      return { campaignType: type, values, total: totalVal };
    }).sort((a,b) => {
      const av = typeof a.total === 'object' ? (a.total.cost + a.total.revenue) : a.total;
      const bv = typeof b.total === 'object' ? (b.total.cost + b.total.revenue) : b.total;
      return bv - av;
    });

    // Totals per day
    const totals = dayKeys.map(k => {
      if (activeMetric === 'ROAS') {
        let cSum = 0, rSum = 0;
        Object.values(byTypeDate).forEach(map => { const cell = map[k]; if (cell) { cSum += cell.cost; rSum += cell.revenue; } });
        return cSum > 0 ? rSum / cSum : 0;
      }
      if (activeMetric === 'Cost+Revenue') {
        let cSum = 0, rSum = 0;
        Object.values(byTypeDate).forEach(map => { const cell = map[k]; if (cell) { cSum += cell.cost; rSum += cell.revenue; } });
        return { cost: cSum, revenue: rSum };
      }
      if (activeMetric === 'CPS') {
        let cSum = 0, sSum = 0;
        Object.values(byTypeDate).forEach(map => { const cell = map[k]; if (cell) { cSum += cell.cost; sSum += (cell.sessions || 0); } });
        return sSum > 0 ? cSum / sSum : 0;
      }
      return rows.reduce((s, r) => s + (r.values[dayKeys.indexOf(k)] || 0), 0);
    });
    const grand = activeMetric === 'ROAS'
      ? (() => { let c=0,r=0; dayKeys.forEach(k=>{ Object.values(byTypeDate).forEach(m=>{const cell=m[k]; if(cell){c+=cell.cost;r+=cell.revenue;}});}); return c>0? r/c:0; })()
      : activeMetric === 'Cost+Revenue'
        ? (() => { let c=0,r=0; dayKeys.forEach(k=>{ Object.values(byTypeDate).forEach(m=>{const cell=m[k]; if(cell){c+=cell.cost;r+=cell.revenue;}});}); return { cost: c, revenue: r }; })()
        : activeMetric === 'CPS'
          ? (() => { let c=0,s=0; dayKeys.forEach(k=>{ Object.values(byTypeDate).forEach(m=>{const cell=m[k]; if(cell){c+=cell.cost;s+=(cell.sessions||0);}});}); return s>0? c/s:0; })()
        : rows.reduce((s,r)=> s + (r.total||0), 0);
    totals.push(grand);

    const headers = ['Campaign Type', ...dateList.map(d => d.toLocaleDateString('en-GB', { day:'numeric', month:'short' })), 'Total'];
    // Attach children (campaign list per type) with daily values for UI expansion
    const childrenByTypeDate = {};
    allTimeUnifiedGaData.forEach(item => {
      const dt = item.date ? new Date(item.date) : null;
      if (!dt || dt < start || dt > end) return;
      const key = typeof item.date === 'string' ? item.date.split('T')[0] : formatLocalDateYMD(dt);
      const type = item.campaign_type || 'Unknown';
      const name = item.campaign_name || item.campaignName || 'Unnamed';
      if (!childrenByTypeDate[type]) childrenByTypeDate[type] = {};
      if (!childrenByTypeDate[type][name]) childrenByTypeDate[type][name] = {};
      if (!childrenByTypeDate[type][name][key]) childrenByTypeDate[type][name][key] = { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, sessions: 0 };
      const v = getVals(item);
      const cell = childrenByTypeDate[type][name][key];
      cell.cost += v.cost;
      cell.revenue += v.revenue;
      cell.clicks += v.clicks;
      cell.impressions += v.impressions;
      cell.conversions += v.conversions;
      cell.sessions += v.sessions;
    });

    rows.forEach(r => {
      const mapByName = childrenByTypeDate[r.campaignType] || {};
      r.children = Object.keys(mapByName).map(cn => {
        let sumCost = 0, sumRevenue = 0, totalVal = 0;
        const values = dayKeys.map(k => {
          const cell = mapByName[cn][k] || { cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, sessions: 0 };
          sumCost += cell.cost; sumRevenue += cell.revenue;
          let v = 0;
          switch (activeMetric) {
            case 'Clicks': v = cell.clicks; break;
            case 'Impressions': v = cell.impressions; break;
            case 'Conversions': v = cell.conversions; break;
            case 'Revenue': v = cell.revenue; break;
            case 'Cost+Revenue': v = { cost: cell.cost, revenue: cell.revenue }; break;
            case 'CPS': v = cell.sessions > 0 ? cell.cost / cell.sessions : 0; break;
            case 'ROAS': v = cell.cost > 0 ? cell.revenue / cell.cost : 0; break;
            case 'Cost':
            default: v = cell.cost; break;
          }
          totalVal += typeof v === 'object' ? 0 : v;
          return v;
        });
        const total = activeMetric === 'ROAS' ? (sumCost > 0 ? sumRevenue / sumCost : 0) : (activeMetric === 'Cost+Revenue' ? { cost: sumCost, revenue: sumRevenue } : totalVal);
        return { name: cn, values, total };
      }).sort((a,b)=> b.total - a.total);
    });

    return { headers, rows, totals };
  }, [allTimeUnifiedGaData, activeMetric]);

  // Weekly (Mon-Sun) pivot — last 12 weeks ending with the week of yesterday
  const weeklyPivotTableData = useMemo(() => {
    if (!allTimeUnifiedGaData || allTimeUnifiedGaData.length === 0) {
      return { headers: [], rows: [], totals: [] };
    }

    const WEEK_COUNT = 12;
    const startOfWeek = (d) => {
      const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const dow = (x.getDay() + 6) % 7; // Monday = 0
      x.setDate(x.getDate() - dow);
      return x;
    };

    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    end.setDate(end.getDate() - 1); // yesterday
    const lastWeekStart = startOfWeek(end);

    const weekStarts = [];
    for (let i = WEEK_COUNT - 1; i >= 0; i--) {
      const ws = new Date(lastWeekStart);
      ws.setDate(ws.getDate() - i * 7);
      weekStarts.push(ws);
    }
    const rangeStart = weekStarts[0];
    const weekKeys = weekStarts.map(d => formatLocalDateYMD(d));

    // Local-midnight date from a raw API date value (avoids UTC shifting)
    const toLocalDay = (raw) => {
      if (!raw) return null;
      const ymd = typeof raw === 'string' ? raw.split('T')[0] : formatLocalDateYMD(new Date(raw));
      const [y, m, d] = ymd.split('-').map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    };

    const getVals = (item) => ({
      cost: Number(item.cost || 0),
      revenue: Number(item.conversion_value || 0),
      clicks: Number(item.clicks || 0),
      impressions: Number(item.impressions || 0),
      conversions: Number(item.conversions || 0),
      sessions: Number(item.ga4_sessions || item.sessions || 0)
    });
    const emptyCell = () => ({ cost: 0, revenue: 0, clicks: 0, impressions: 0, conversions: 0, sessions: 0 });
    const addTo = (cell, v) => {
      cell.cost += v.cost;
      cell.revenue += v.revenue;
      cell.clicks += v.clicks;
      cell.impressions += v.impressions;
      cell.conversions += v.conversions;
      cell.sessions += v.sessions;
    };

    const byTypeWeek = {};
    const childrenByTypeWeek = {};

    allTimeUnifiedGaData.forEach(item => {
      const day = toLocalDay(item.date);
      if (!day || day < rangeStart || day > end) return;
      const wk = formatLocalDateYMD(startOfWeek(day));
      const type = item.campaign_type || 'Unknown';
      const name = item.campaign_name || item.campaignName || 'Unnamed';
      const v = getVals(item);

      if (!byTypeWeek[type]) byTypeWeek[type] = {};
      if (!byTypeWeek[type][wk]) byTypeWeek[type][wk] = emptyCell();
      addTo(byTypeWeek[type][wk], v);

      if (!childrenByTypeWeek[type]) childrenByTypeWeek[type] = {};
      if (!childrenByTypeWeek[type][name]) childrenByTypeWeek[type][name] = {};
      if (!childrenByTypeWeek[type][name][wk]) childrenByTypeWeek[type][name][wk] = emptyCell();
      addTo(childrenByTypeWeek[type][name][wk], v);
    });

    const metricOf = (cell) => {
      switch (activeMetric) {
        case 'Clicks': return cell.clicks;
        case 'Impressions': return cell.impressions;
        case 'Conversions': return cell.conversions;
        case 'Sessions': return cell.sessions;
        case 'Revenue': return cell.revenue;
        case 'Cost+Revenue': return { cost: cell.cost, revenue: cell.revenue };
        case 'CPS': return cell.sessions > 0 ? cell.cost / cell.sessions : 0;
        case 'CPC': return cell.clicks > 0 ? cell.cost / cell.clicks : 0;
        case 'ROAS': return cell.cost > 0 ? cell.revenue / cell.cost : 0;
        case 'Cost':
        default: return cell.cost;
      }
    };
    // Totals must be re-derived from summed cells for ratio metrics, not summed
    const totalOf = (sum, running) => {
      if (activeMetric === 'ROAS') return sum.cost > 0 ? sum.revenue / sum.cost : 0;
      if (activeMetric === 'CPC') return sum.clicks > 0 ? sum.cost / sum.clicks : 0;
      if (activeMetric === 'CPS') return sum.sessions > 0 ? sum.cost / sum.sessions : 0;
      if (activeMetric === 'Cost+Revenue') return { cost: sum.cost, revenue: sum.revenue };
      return running;
    };

    const rows = Object.keys(byTypeWeek).map(type => {
      const sum = emptyCell();
      let running = 0;
      const values = weekKeys.map(k => {
        const cell = byTypeWeek[type][k] || emptyCell();
        addTo(sum, cell);
        const val = metricOf(cell);
        running += typeof val === 'object' ? 0 : val;
        return val;
      });
      return { campaignType: type, values, total: totalOf(sum, running) };
    }).sort((a, b) => {
      const av = typeof a.total === 'object' ? (a.total.cost + a.total.revenue) : a.total;
      const bv = typeof b.total === 'object' ? (b.total.cost + b.total.revenue) : b.total;
      return bv - av;
    });

    rows.forEach(r => {
      const mapByName = childrenByTypeWeek[r.campaignType] || {};
      r.children = Object.keys(mapByName).map(cn => {
        const sum = emptyCell();
        let running = 0;
        const values = weekKeys.map(k => {
          const cell = mapByName[cn][k] || emptyCell();
          addTo(sum, cell);
          const v = metricOf(cell);
          running += typeof v === 'object' ? 0 : v;
          return v;
        });
        return { name: cn, values, total: totalOf(sum, running) };
      }).sort((a, b) => {
        const av = typeof a.total === 'object' ? (a.total.cost + a.total.revenue) : a.total;
        const bv = typeof b.total === 'object' ? (b.total.cost + b.total.revenue) : b.total;
        return bv - av;
      });
    });

    // Column totals (across campaign types) per week
    const columnSums = weekKeys.map(k => {
      const sum = emptyCell();
      Object.values(byTypeWeek).forEach(map => { if (map[k]) addTo(sum, map[k]); });
      return sum;
    });
    const totals = columnSums.map(sum => metricOf(sum));
    const grandSum = emptyCell();
    columnSums.forEach(s => addTo(grandSum, s));
    totals.push(metricOf(grandSum));

    const labelFor = (ws) => {
      const we = new Date(ws);
      we.setDate(we.getDate() + 6);
      const capped = we > end ? end : we;
      const sameMonth = ws.getMonth() === capped.getMonth();
      const startLabel = sameMonth
        ? String(ws.getDate())
        : ws.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      const endLabel = capped.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return `${startLabel}-${endLabel}`;
    };
    const headers = ['Campaign Type', ...weekStarts.map(labelFor), 'Total'];

    return { headers, rows, totals };
  }, [allTimeUnifiedGaData, activeMetric]);

  const processedTopKeywords = useMemo(() => {
    if (!topKeywordsData || topKeywordsData.length === 0) {
      return [];
    }

    const keywordSummary = {};

    topKeywordsData.forEach(item => {
      const keywordText = item.keyword_text;
      if (!keywordText || keywordText === '(not set)' || keywordText === '--') return;

      if (!keywordSummary[keywordText]) {
        keywordSummary[keywordText] = {
          keyword: keywordText,
          cost: 0,
          clicks: 0,
          impressions: 0,
          conversions: 0,
        };
      }

      const summary = keywordSummary[keywordText];
      summary.cost += parseFloat(item.cost || 0);
      summary.clicks += parseInt(item.clicks || 0, 10);
      summary.impressions += parseInt(item.impressions || 0, 10);
      summary.conversions += parseInt(item.conversions || 0, 10);
    });
    
    const keywordsArray = Object.values(keywordSummary).map(k => ({
      ...k,
      ctr: k.impressions > 0 ? (k.clicks / k.impressions) * 100 : 0,
      cpc: k.clicks > 0 ? k.cost / k.clicks : 0,
    }));

    // Sort by cost and take top 50
    return keywordsArray.sort((a, b) => b.cost - a.cost).slice(0, 50);
  }, [topKeywordsData]);

  const brandImpressionShareMetrics = useMemo(() => {
    if (!impressionShareData || impressionShareData.length === 0) {
      return { searchTop: 0, searchLostRank: 0, searchLostBudget: 0 };
    }
    
    const searchTopMetrics = { total: 0, count: 0 };
    const searchLostRankMetrics = { total: 0, count: 0 };
    const searchLostBudgetMetrics = { total: 0, count: 0 };

    impressionShareData.forEach(item => {
        if (item.search_top_impression_share > 0) {
            searchTopMetrics.total += item.search_top_impression_share;
            searchTopMetrics.count += 1;
        }
        if (item.search_rank_lost_impression_share > 0) {
            searchLostRankMetrics.total += item.search_rank_lost_impression_share;
            searchLostRankMetrics.count += 1;
        }
        if (item.search_budget_lost_impression_share > 0) {
            searchLostBudgetMetrics.total += item.search_budget_lost_impression_share;
            searchLostBudgetMetrics.count += 1;
        }
    });

    return {
      searchTop: searchTopMetrics.count > 0 ? (searchTopMetrics.total / searchTopMetrics.count) * 100 : 0,
      searchLostRank: searchLostRankMetrics.count > 0 ? (searchLostRankMetrics.total / searchLostRankMetrics.count) * 100 : 0,
      searchLostBudget: searchLostBudgetMetrics.count > 0 ? (searchLostBudgetMetrics.total / searchLostBudgetMetrics.count) * 100 : 0,
    };
  }, [impressionShareData]);

  const processedConversionActions = useMemo(() => {
    if (!conversionActionsData || conversionActionsData.length === 0) {
      return [];
    }
    const summary = conversionActionsData.reduce((acc, item) => {
      if (!item.conversion_action_name) return acc;

      if (!acc[item.conversion_action_name]) {
        acc[item.conversion_action_name] = {
          name: item.conversion_action_name,
          category: item.conversion_action_category,
          conversions: 0,
          value: 0,
        };
      }
      
      acc[item.conversion_action_name].conversions += item.conversions;
      acc[item.conversion_action_name].value += item.conversion_value;
      
      return acc;
    }, {});

    return Object.values(summary).sort((a, b) => b.conversions - a.conversions);
  }, [conversionActionsData]);

  const fetchData = useCallback(async (force = false) => {
    if (!force && lastFetchTime && Date.now() - lastFetchTime < 5 * 60 * 1000) {
      return;
    }

    try {
    setLoading(true);
      setTableLoading({
        performance: true,
        campaigns: true,
        keywords: true
      });
      
      const brandId = localStorage.getItem('currentBrandId');

      // Check for special dummy brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        console.log('Using dummy data for special brand:', brandId);
        const dummyData = generateDummyGoogleAdsData();
        setData(dummyData);
        setCombinedCampaignData(generateDummyCombinedCampaignData());
        setDailySummaryData(generateDummyDailySummaryData());
        setDevicePerformanceData(generateDummyDevicePerformanceData());
        setTopKeywordsData(generateDummyTopKeywordsData());
        setMonthlyPerformanceApiData(generateDummyMonthlyPerformanceData());
        setImpressionShareData(generateDummyImpressionShareData());
        setConversionActionsData(generateDummyConversionActionsData());
        setUnifiedGaData(generateDummyUnifiedGoogleAdsGa4Data());
        setAllTimeUnifiedGaData(generateDummyUnifiedGoogleAdsGa4Data());
        setLastFetchTime(Date.now());
        setLoading(false);
        setTableLoading({ performance: false, campaigns: false, keywords: false, topKeywords: false, devicePerformance: false, impressionShare: false, conversionActions: false, unifiedGaPerformance: false });
        return;
      }

      const response = await fetch(`https://api.glowytics.com/api/google-ads/dashboard${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})); // JSON parse hatasına karşı güvence
        toast.error(errorData.details || errorData.error || 'Failed to fetch dashboard data');
        setError(errorData.details || errorData.error || 'Failed to fetch dashboard data');
        // Hata durumunda state'i boşaltıp fonksiyondan çık
        setData({ summary: {}, campaigns: [], performance: { daily: { labels: [], datasets: [] }, devices: { labels: [], datasets: [] }, geos: { labels: [], datasets: [] } } });
        setLoading(false);
        setTableLoading({ performance: false, campaigns: false, keywords: false });
        return;
      }

      const result = await response.json();
      console.log('Received data:', result);
      // Use startTransition for non-blocking UI updates
      startTransition(() => {
      setData(result);
      });
      setLastFetchTime(Date.now());

      // API'den veri gelmiyorsa test verileri ekleyelim
      if (!result || !result.campaigns || !Array.isArray(result.campaigns) || result.campaigns.length === 0) {
        console.log('API returned empty data, adding test data');
        
        // Test verileri
        const testData = {
          campaigns: [
            {
              id: 'camp1',
              name: 'Search Campaign',
              type: 'AdvertisingChannelType.SEARCH',
              status: 'Active',
              budget: 10000,
              spend: 8500,
              impressions: 85000,
              clicks: 3500,
              ctr: 4.12,
              cpc: 2.43,
              conversions: 110,
              conversionRate: 3.14,
              revenue: 22000,
              roas: 2.59
            },
            {
              id: 'camp2',
              name: 'Display Campaign',
              type: 'AdvertisingChannelType.DISPLAY',
              status: 'Active',
              budget: 8000,
              spend: 6800,
              impressions: 120000,
              clicks: 4200,
              ctr: 3.5,
              cpc: 1.62,
              conversions: 75,
              conversionRate: 1.79,
              revenue: 16000,
              roas: 2.35
            }
          ]
        };
        
        // Test verilerini API yanıtına ekleyelim
        setData(testData);
      }

  } catch (error) {
      // Log to console for debugging, but suppress noisy backend warnings
      console.error('Error:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) {
        toast.error(msg);
        setError(msg);
      }
  } finally {
      setLoading(false);
      setTableLoading({
        performance: false,
        campaigns: false,
        keywords: false
      });
    }
  }, [dateRange, buildQueryParams]);

  const fetchMonthlyCampaignPerformance = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setMonthlyPerformanceApiData(generateDummyMonthlyPerformanceData());
        return;
      }
      
      const monthlyAccParam = gaAccountFilter && gaAccountFilter !== 'all' ? `?accountId=${gaAccountFilter}` : '';
      const response = await fetch(`https://api.glowytics.com/api/google-ads/monthly-by-campaign-type${monthlyAccParam}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch monthly campaign performance data');
      }

      const result = await response.json();
      startTransition(() => {
      setMonthlyPerformanceApiData(result);
      });
      
    } catch (error) {
      console.error('Error fetching monthly campaign performance data:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error(msg);
    }
  }, []);

  const fetchTopKeywordsData = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, topKeywords: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setTopKeywordsData([
          { keyword: 'kadın elbise online', matchType: 'BROAD', clicks: 2450, impressions: 85000, cost: 12500, conversions: 185, conversionValue: 45000, ctr: 2.88, cpc: 5.10, conversionRate: 7.55, roas: 3.6 },
          { keyword: 'erkek gömlek slim fit', matchType: 'PHRASE', clicks: 1890, impressions: 62000, cost: 9800, conversions: 142, conversionValue: 32000, ctr: 3.05, cpc: 5.19, conversionRate: 7.51, roas: 3.27 },
          { keyword: 'kışlık mont erkek', matchType: 'EXACT', clicks: 1650, impressions: 45000, cost: 8200, conversions: 125, conversionValue: 38000, ctr: 3.67, cpc: 4.97, conversionRate: 7.58, roas: 4.63 },
          { keyword: 'spor ayakkabı kadın', matchType: 'BROAD', clicks: 1420, impressions: 52000, cost: 7500, conversions: 98, conversionValue: 22000, ctr: 2.73, cpc: 5.28, conversionRate: 6.90, roas: 2.93 },
          { keyword: 'deri çanta modelleri', matchType: 'PHRASE', clicks: 1180, impressions: 38000, cost: 6200, conversions: 88, conversionValue: 18500, ctr: 3.11, cpc: 5.25, conversionRate: 7.46, roas: 2.98 },
          { keyword: 'sezon sonu indirim', matchType: 'EXACT', clicks: 2100, impressions: 72000, cost: 11000, conversions: 165, conversionValue: 42000, ctr: 2.92, cpc: 5.24, conversionRate: 7.86, roas: 3.82 },
          { keyword: 'kadın pantolon yüksek bel', matchType: 'BROAD', clicks: 980, impressions: 35000, cost: 5100, conversions: 72, conversionValue: 15800, ctr: 2.80, cpc: 5.20, conversionRate: 7.35, roas: 3.10 },
          { keyword: 'erkek kaban kış', matchType: 'PHRASE', clicks: 850, impressions: 28000, cost: 4500, conversions: 65, conversionValue: 19500, ctr: 3.04, cpc: 5.29, conversionRate: 7.65, roas: 4.33 },
          { keyword: 'yeni sezon giyim', matchType: 'BROAD', clicks: 1350, impressions: 48000, cost: 7000, conversions: 105, conversionValue: 28000, ctr: 2.81, cpc: 5.19, conversionRate: 7.78, roas: 4.00 },
          { keyword: 'online moda alışveriş', matchType: 'EXACT', clicks: 1100, impressions: 32000, cost: 5800, conversions: 92, conversionValue: 24500, ctr: 3.44, cpc: 5.27, conversionRate: 8.36, roas: 4.22 }
        ]);
        setTableLoading(prev => ({ ...prev, topKeywords: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/keyword-full-performance${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch top keywords data');
      }

      const result = await response.json();
      setTopKeywordsData(result);
      
    } catch (error) {
      console.error('Error fetching top keywords data:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error(msg);
      setTopKeywordsData([]);
    } finally {
      setTableLoading(prev => ({ ...prev, topKeywords: false }));
    }
  }, [dateRange, buildQueryParams]);

  // fetchCombinedPerformanceData fonksiyonunu güncelle
  const fetchCombinedPerformanceData = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, performance: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        const dummyCombined = [
          { source: 'SEARCH', count: 450, sessions: 28500, ga4_transaction: 1250, ga4_revenue: 425000, google_ads_clicks: 32000, google_ads_cost: 145000, google_ads_transaction: 1180, google_ads_revenue: 398000, google_ads_impression: 850000, cpc: 4.53, cps: 5.09, campaignCount: 8 },
          { source: 'PERFORMANCE_MAX', count: 380, sessions: 22000, ga4_transaction: 980, ga4_revenue: 342000, google_ads_clicks: 25500, google_ads_cost: 118000, google_ads_transaction: 920, google_ads_revenue: 315000, google_ads_impression: 1250000, cpc: 4.63, cps: 5.36, campaignCount: 5 },
          { source: 'SHOPPING', count: 220, sessions: 15000, ga4_transaction: 620, ga4_revenue: 185000, google_ads_clicks: 18000, google_ads_cost: 72000, google_ads_transaction: 580, google_ads_revenue: 168000, google_ads_impression: 620000, cpc: 4.00, cps: 4.80, campaignCount: 3 },
          { source: 'DISPLAY', count: 180, sessions: 8500, ga4_transaction: 185, ga4_revenue: 52000, google_ads_clicks: 12000, google_ads_cost: 38000, google_ads_transaction: 165, google_ads_revenue: 45000, google_ads_impression: 2800000, cpc: 3.17, cps: 4.47, campaignCount: 4 },
          { source: 'VIDEO', count: 120, sessions: 5200, ga4_transaction: 95, ga4_revenue: 28000, google_ads_clicks: 8500, google_ads_cost: 25000, google_ads_transaction: 85, google_ads_revenue: 24000, google_ads_impression: 1500000, cpc: 2.94, cps: 4.81, campaignCount: 2 },
          { source: 'TOTAL', count: 1350, sessions: 79200, ga4_transaction: 3130, ga4_revenue: 1032000, google_ads_clicks: 96000, google_ads_cost: 398000, google_ads_transaction: 2930, google_ads_revenue: 950000, google_ads_impression: 7020000, cpc: 4.15, cps: 5.03, campaignCount: 22 }
        ];
        setCombinedCampaignData(dummyCombined);
        setTableLoading(prev => ({ ...prev, performance: false }));
        return;
      }
      
      // Tarih aralığını URL parametresi olarak ekle
      const response = await fetch(`https://api.glowytics.com/api/google-ads/combined-campaign-performance${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch combined performance data');
      }

      const result = await response.json();
      
      // Ham veriyi detaylı bir şekilde konsola yazdır
      console.log(`===== COMBINED CAMPAIGN PERFORMANCE API RESPONSE (${dateRange}) =====`);
      console.log('Raw API Response:', result);
      
      // Veri yapısını analiz et
      console.log('Data Type:', typeof result);
      console.log('Is Array:', Array.isArray(result));
      console.log('Data Length:', result.length);
      
      // İlk kaydı detaylı inceleyelim
      if (result.length > 0) {
        console.log('First Record Fields:', Object.keys(result[0]));
        console.log('First Record Values:', result[0]);
      }
      
      // Tüm kayıtların campaign_type değerlerini listele
      if (Array.isArray(result) && result.length > 0) {
        console.log('All Campaign Type Values:');
        const campaignTypes = result.map(item => item.campaignType);
        console.log(campaignTypes);
        
        // Benzersiz campaign_type değerlerini göster
        const uniqueCampaignTypes = [...new Set(campaignTypes)];
        console.log('Unique Campaign Type Values:', uniqueCampaignTypes);
      }
      
      console.log(`===== COMBINED CAMPAIGN PERFORMANCE API RESPONSE (${dateRange}) END =====`);
      
      // Eğer API'den veri gelmezse veya boş bir dizi dönerse
      if (!result || !Array.isArray(result) || result.length === 0) {
        console.log(`No data returned for date range: ${dateRange}`);
        combinedCampaignRawRef.current = [];
        setCombinedCampaignData([]);
        return;
      }

      combinedCampaignRawRef.current = result;
      const rowsForAgg = filterRowsByYatasCampaignName(result, brandId, yatasCampaignFilter, (r) => r.campaignName);
      const finalData = aggregateCombinedCampaignPerformanceRows(rowsForAgg);

      if (finalData.length === 0) {
        console.log(`No grouped data for date range: ${dateRange}`);
        setCombinedCampaignData([]);
        return;
      }

      console.log(`Grouped data for date range (${dateRange}):`, finalData);
      startTransition(() => {
        setCombinedCampaignData(finalData);
      });
      
    } catch (error) {
      console.error(`Error fetching combined performance data for date range (${dateRange}):`, error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error('Failed to load campaign performance data');
      
      // Hata durumunda test verileri ekleyelim
      console.log(`Adding test data for date range (${dateRange}) due to API error`);
      const testData = [
        {
          source: 'AdvertisingChannelType.SEARCH',
          count: 10,
          sessions: 5000,
          ga4_transaction: 150,
          ga4_revenue: 75000,
          google_ads_clicks: 2500,
          google_ads_cost: 15000,
          google_ads_transaction: 120,
          google_ads_revenue: 60000,
          google_ads_impression: 50000,
          cpc: 6, // 15000 / 2500 = 6
          cps: 3, // 15000 / 5000 = 3
          date_count: dateRange === 'today' || dateRange === 'yesterday' ? 1 : 
                     dateRange === 'last7' ? 7 : 
                     dateRange === 'last30' ? 30 : 30,
          campaignCount: 5, // Test için kampanya sayısı
          dates: Array(dateRange === 'today' || dateRange === 'yesterday' ? 1 : 
                       dateRange === 'last7' ? 7 : 
                       dateRange === 'last30' ? 30 : 30).fill('2023-01-01')
        },
        {
          source: 'TOTAL',
          count: 10,
          sessions: 5000,
          ga4_transaction: 150,
          ga4_revenue: 75000,
          google_ads_clicks: 2500,
          google_ads_cost: 15000,
          google_ads_transaction: 120,
          google_ads_revenue: 60000,
          google_ads_impression: 50000,
          cpc: 6, // 15000 / 2500 = 6
          cps: 3, // 15000 / 5000 = 3
          date_count: dateRange === 'today' || dateRange === 'yesterday' ? 1 : 
                     dateRange === 'last7' ? 7 : 
                     dateRange === 'last30' ? 30 : 30,
          campaignCount: 5, // Test için toplam kampanya sayısı
        }
      ];
      
      setCombinedCampaignData(testData);
    } finally {
      setTableLoading(prev => ({ ...prev, performance: false }));
    }
  }, [dateRange, buildQueryParams]);

  useEffect(() => {
    const raw = combinedCampaignRawRef.current;
    if (!raw || !Array.isArray(raw) || raw.length === 0) return;
    const bid = localStorage.getItem('currentBrandId');
    const accountFiltered = gaAccountFilter === 'all' ? raw : raw.filter(r => r.accountId === gaAccountFilter);
    const rows = filterRowsByYatasCampaignName(accountFiltered, bid, yatasCampaignFilter, (r) => r.campaignName);
    const finalData = aggregateCombinedCampaignPerformanceRows(rows);
    startTransition(() => {
      setCombinedCampaignData(finalData.length ? finalData : []);
    });
  }, [yatasCampaignFilter, gaAccountFilter]);

  // fetchUnifiedAdPlatformData fonksiyonunu ekleyelim
  const fetchUnifiedAdPlatformData = useCallback(async () => {
    try {
      console.log(`===== FETCHING UNIFIED AD PLATFORM DAILY SUMMARY (${dateRange}) =====`);
      setTableLoading(prev => ({ ...prev, unifiedAd: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand - just log and return since this function only logs
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        console.log('Unified Ad Platform Dummy Data:', generateDummyUnifiedAdPlatformData());
        setTableLoading(prev => ({ ...prev, unifiedAd: false }));
        return;
      }
      
      // API endpoint'ini çağıralım
      const response = await fetch(`https://api.glowytics.com/api/google-ads/unified-ad-platform-daily-summary${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unified ad platform data');
      }

      const result = await response.json();
      
      // Sonuçları detaylı bir şekilde konsolda gösterelim
      console.log('Unified Ad Platform Daily Summary - Raw API Response:', result);
      console.log('Data Type:', typeof result);
      console.log('Is Array:', Array.isArray(result));
      console.log('Data Length:', result.length);
      
      // İlk kaydı detaylı inceleyelim
      if (result.length > 0) {
        console.log('First Record Fields:', Object.keys(result[0]));
        console.log('First Record Sample:', result[0]);
        
        // Benzersiz reklam kaynaklarını listeleyelim (Google Ads, Meta, vb.)
        const sources = [...new Set(result.map(item => item.source))];
        console.log('Unique Ad Sources:', sources);
        
        // Tarih bazında gruplayalım
        const groupedByDate = result.reduce((acc, item) => {
          const date = typeof item.date === 'string' ? item.date.split('T')[0] : formatLocalDateYMD(new Date(item.date));
          if (!acc[date]) acc[date] = [];
          acc[date].push(item);
          return acc;
        }, {});
        
        console.log('Data grouped by date:', groupedByDate);
        
        // Kaynak bazında toplam değerleri hesaplayalım
        const totalsBySource = {};
        result.forEach(item => {
          if (!totalsBySource[item.source]) {
            totalsBySource[item.source] = {
              source: item.source,
              totalImpressions: 0,
              totalClicks: 0,
              totalCost: 0,
              totalConversions: 0,
              totalConversionValue: 0
            };
          }
          
          totalsBySource[item.source].totalImpressions += item.totalImpressions;
          totalsBySource[item.source].totalClicks += item.totalClicks;
          totalsBySource[item.source].totalCost += item.totalCost;
          totalsBySource[item.source].totalConversions += item.totalConversions;
          totalsBySource[item.source].totalConversionValue += item.totalConversionValue;
        });
        
        console.log('Totals by Source:', totalsBySource);
      }
      
      console.log(`===== UNIFIED AD PLATFORM DAILY SUMMARY DATA (${dateRange}) END =====`);
      
      // State'e kaydedebilir veya sadece console'da görebiliriz
      // setUnifiedPlatformData(result);
      
    } catch (error) {
      console.error(`Error fetching unified ad platform data (${dateRange}):`, error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error('Failed to load unified ad platform data');
    } finally {
      setTableLoading(prev => ({ ...prev, unifiedAd: false }));
    }
  }, [dateRange, buildQueryParams]);

  // Google Ads Daily Summary verilerini çeken fonksiyon
  const fetchDailySummaryData = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, dailySummary: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        const today = new Date();
        const dummyDaily = [];
        for (let i = 29; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const baseCost = 12000 + Math.random() * 6000;
          dummyDaily.push({
            date: date.toISOString().split('T')[0],
            cost: Math.round(baseCost),
            clicks: Math.round(baseCost * 0.72),
            impressions: Math.round(baseCost * 22),
            conversions: Math.round(baseCost * 0.028),
            conversionValue: Math.round(baseCost * 3.1),
            ctr: (2.5 + Math.random() * 1.2).toFixed(2),
            cpc: (4.2 + Math.random() * 1.5).toFixed(2),
            conversionRate: (6.5 + Math.random() * 2.5).toFixed(2)
          });
        }
        setDailySummaryData(dummyDaily);
        setTableLoading(prev => ({ ...prev, dailySummary: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/daily-summary${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Google Ads daily summary data');
      }

      const result = await response.json();
      console.log('Google Ads Daily Summary Data:', result);
      
      setDailySummaryData(result);
      
    } catch (error) {
      console.error('Error fetching Google Ads daily summary:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error('Failed to load Google Ads daily summary data');
    } finally {
      setTableLoading(prev => ({ ...prev, dailySummary: false }));
    }
  }, [dateRange, buildQueryParams]);

  // Function to fetch unified Google Ads & GA4 performance data
  const fetchUnifiedGoogleAdsGa4Performance = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, unifiedGaPerformance: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setUnifiedGaData(generateDummyUnifiedGoogleAdsGa4Data());
        setTableLoading(prev => ({ ...prev, unifiedGaPerformance: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unified Google Ads & GA4 performance data');
      }

      const result = await response.json();
      console.log('Unified Google Ads & GA4 Performance Data:', result);
      
      setUnifiedGaData(result);
      
    } catch (error) {
      console.error('Error fetching unified Google Ads & GA4 performance data:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error('Failed to load unified Google Ads & GA4 performance data');
    } finally {
      setTableLoading(prev => ({ ...prev, unifiedGaPerformance: false }));
    }
  }, [dateRange, buildQueryParams]);

  const fetchAllTimeUnifiedGoogleAdsGa4Performance = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        // Generate 90 days of data for all-time view
        const today = new Date();
        const allTimeData = [];
        for (let i = 89; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const baseCost = 5000 + Math.random() * 3000;
          allTimeData.push({
            date: date.toISOString().split('T')[0],
            cost: Math.round(baseCost),
            clicks: Math.round(baseCost * 0.75),
            impressions: Math.round(baseCost * 18),
            conversions: Math.round(baseCost * 0.025),
            conversionValue: Math.round(baseCost * 3.2),
            ga4Sessions: Math.round(baseCost * 0.85),
            ga4Users: Math.round(baseCost * 0.65),
            ga4Revenue: Math.round(baseCost * 3.5),
            ga4Transactions: Math.round(baseCost * 0.028)
          });
        }
        setAllTimeUnifiedGaData(allTimeData);
        return;
      }
      
      const allTimeAccParam = gaAccountFilter && gaAccountFilter !== 'all' ? `?accountId=${gaAccountFilter}` : '';
      const response = await fetch(`https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance${allTimeAccParam}`, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all-time unified Google Ads & GA4 performance data');
      }

      const result = await response.json();
      setAllTimeUnifiedGaData(result);

    } catch (error) {
      console.error('Error fetching all-time unified Google Ads & GA4 performance data:', error);
      const msg = (error && error.message) ? error.message : String(error);
      if (!shouldSuppressBackendError(msg)) toast.error('Failed to load all-time data for performance trends table');
    }
  }, [gaAccountFilter]);

  const fetchDevicePerformance = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, devicePerformance: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setDevicePerformanceData([
          { device: 'MOBILE', clicks: 68000, impressions: 4200000, cost: 285000, conversions: 2100, conversionValue: 680000, ctr: 1.62, cpc: 4.19, conversionRate: 3.09 },
          { device: 'DESKTOP', clicks: 22000, impressions: 2100000, cost: 98000, conversions: 720, conversionValue: 245000, ctr: 1.05, cpc: 4.45, conversionRate: 3.27 },
          { device: 'TABLET', clicks: 6000, impressions: 720000, cost: 25000, conversions: 180, conversionValue: 58000, ctr: 0.83, cpc: 4.17, conversionRate: 3.00 }
        ]);
        setTableLoading(prev => ({ ...prev, devicePerformance: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/device-performance${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch device performance data');
      }

      const result = await response.json();
      setDevicePerformanceData(result);
      
    } catch (error) {
      console.error('Error fetching device performance data:', error);
      setDevicePerformanceData([]);
    } finally {
      setTableLoading(prev => ({ ...prev, devicePerformance: false }));
    }
  }, [dateRange, buildQueryParams]);

  const fetchImpressionShareData = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, impressionShare: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setImpressionShareData(generateDummyImpressionShareData());
        setTableLoading(prev => ({ ...prev, impressionShare: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/impression-share${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch impression share data');
      }

      const result = await response.json();
      setImpressionShareData(result);
      
    } catch (error) {
      console.error('Error fetching impression share data:', error);
      toast.error('Failed to load impression share data');
      setImpressionShareData([]);
    } finally {
      setTableLoading(prev => ({ ...prev, impressionShare: false }));
    }
  }, [dateRange, buildQueryParams]);

  const fetchConversionActionsData = useCallback(async () => {
    try {
      setTableLoading(prev => ({ ...prev, conversionActions: true }));
      const brandId = localStorage.getItem('currentBrandId');
      
      // Dummy data for special brand
      if (brandId === SPECIAL_DUMMY_BRAND_ID) {
        setConversionActionsData(generateDummyConversionActionsData());
        setTableLoading(prev => ({ ...prev, conversionActions: false }));
        return;
      }
      
      const response = await fetch(`https://api.glowytics.com/api/google-ads/conversion-actions${buildQueryParams()}` , {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversion actions data');
      }

      const result = await response.json();
      setConversionActionsData(result);
      
    } catch (error) {
      console.error('Error fetching conversion actions data:', error);
      toast.error('Failed to load conversion actions data');
      setConversionActionsData([]);
    } finally {
      setTableLoading(prev => ({ ...prev, conversionActions: false }));
    }
  }, [dateRange, buildQueryParams]);

  // useEffect'i güncelleyelim
  useEffect(() => {
    fetchData(true);
    fetchCombinedPerformanceData();
    fetchUnifiedAdPlatformData();
    fetchDailySummaryData();
    fetchUnifiedGoogleAdsGa4Performance();
    fetchDevicePerformance();
    fetchTopKeywordsData();
    fetchImpressionShareData();
    fetchConversionActionsData();
    fetchMonthlyCampaignPerformance();
  }, [dateRange, fetchData, fetchCombinedPerformanceData, fetchUnifiedAdPlatformData, fetchDailySummaryData, fetchUnifiedGoogleAdsGa4Performance, fetchDevicePerformance, fetchTopKeywordsData, fetchImpressionShareData, fetchConversionActionsData, fetchMonthlyCampaignPerformance]);

  // Auto-refresh when a custom range is selected/changed
  useEffect(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      // Force a full refresh cycle so new params apply immediately
      handleRefresh();
    }
  }, [dateRange, customStartDate, customEndDate]);

  // NOTE: Removed duplicate useEffect - these functions are already called in the main useEffect above
  // Only fetch all-time data once on mount (no dateRange dependency)
  useEffect(() => {
    fetchAllTimeUnifiedGoogleAdsGa4Performance();
  }, [fetchAllTimeUnifiedGoogleAdsGa4Performance]);

  const handleRefresh = () => {
    fetchData(true);
    fetchCombinedPerformanceData();
    fetchUnifiedAdPlatformData();
    fetchDailySummaryData(); // Yeni fonksiyonu çağıralım
    fetchUnifiedGoogleAdsGa4Performance();
    fetchAllTimeUnifiedGoogleAdsGa4Performance();
    fetchDevicePerformance();
    fetchMonthlyCampaignPerformance();
    fetchTopKeywordsData();
    fetchImpressionShareData();
    fetchConversionActionsData();
  };

  const formatDeviceName = (deviceType) => {
    if (!deviceType) return 'Other';
    const cleaned = deviceType.toLowerCase().replace('device.', '').replace(/_/g, ' ');
    if (cleaned.includes('mobile')) return 'Mobile';
    if (cleaned.includes('desktop')) return 'Desktop';
    if (cleaned.includes('tablet')) return 'Tablet';
    if (cleaned.includes('tv')) return 'Connected TV';
    return 'Other';
  };

  const getDeviceIcon = (deviceType) => {
    const type = formatDeviceName(deviceType).toLowerCase();
    if (type.includes('mobile')) return <Smartphone className="w-4 h-4 text-gray-500" />;
    if (type.includes('desktop')) return <Monitor className="w-4 h-4 text-gray-500" />;
    if (type.includes('tablet')) return <Tablet className="w-4 h-4 text-gray-500" />;
    if (type.includes('connected tv')) return <Monitor className="w-4 h-4 text-gray-500" />;
    return <HelpCircle className="w-4 h-4 text-gray-500" />;
  };

  // Function to get the appropriate icon for a campaign type
  const getCampaignTypeIcon = (type) => {
    switch (type) {
      case 'Search':
        return <Search className="w-5 h-5 text-blue-600" />;
      case 'Display':
        return <LayoutGrid className="w-5 h-5 text-green-600" />;
      case 'Video':
        return <Play className="w-5 h-5 text-red-600" />;
      case 'Shopping':
        return <ShoppingBag className="w-5 h-5 text-yellow-600" />;
      case 'Performance Max':
        return <Zap className="w-5 h-5 text-purple-600" />;
      case 'Dynamic Search':
        return <Globe className="w-5 h-5 text-orange-600" />;
      case 'App':
        return <Smartphone className="w-5 h-5 text-blue-400" />;
      case 'Demand Gen':
        return <Target className="w-5 h-5 text-pink-600" />;
      case 'Total':
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
      default:
        return <Globe className="w-5 h-5 text-gray-600" />;
    }
  };

  // Function to get the background color for a campaign type
  const getCampaignTypeBackground = (type) => {
    switch (type) {
      case 'Search':
        return 'bg-blue-100';
      case 'Display':
        return 'bg-green-100';
      case 'Video':
        return 'bg-red-100';
      case 'Shopping':
        return 'bg-yellow-100';
      case 'Performance Max':
        return 'bg-purple-100';
      case 'Dynamic Search':
        return 'bg-orange-100';
      case 'App':
        return 'bg-blue-50';
      case 'Demand Gen':
        return 'bg-pink-100';
      case 'Total':
        return 'bg-gray-100';
      default:
        return 'bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f8ff]/70">
        <div className="loading-logo-container">
          <img src="/logoicon.png" alt="Loading" className="loading-logo" />
        </div>
      </div>
    );
  }

  // Calculate totals for the selected campaigns
  const campaignTotals = data.campaigns && Array.isArray(data.campaigns) ? data.campaigns.reduce((totals, campaign) => ({
    spend: (totals.spend || 0) + (campaign.spend || 0),
    impressions: (totals.impressions || 0) + (campaign.impressions || 0),
    clicks: (totals.clicks || 0) + (campaign.clicks || 0),
    conversions: (totals.conversions || 0) + (campaign.conversions || 0),
    revenue: (totals.revenue || 0) + (campaign.revenue || 0)
  }), {}) : {};

  // Card bileşeninin CSS'ini güncelleyelim - Component tanımı dışında kullanılacak ortak sınıf
  const enhancedCardClass = "rounded-3xl overflow-hidden border border-gray-200/80";

  // fetchCampaignData fonksiyonunu güncelle (varsa)
  const fetchCampaignData = (range) => {
    // Mevcut fetchCampaignData fonksiyonunuzu date range parametresi alacak şekilde güncelleyin
    // Örnek:
    setTableLoading({ ...tableLoading, performance: true });
    
    const brandId = localStorage.getItem('currentBrandId');
    
    fetch(`https://api.glowytics.com/api/google-ads/campaign-performance${buildQueryParams(range)}`, {
      credentials: 'include',
      headers: {
        'X-Brand-ID': brandId
      }
    })
    .then(response => response.json())
    .then(data => {
      // Veriyi işleyin...
      setCombinedCampaignData(data);
    })
    .catch(error => {
      console.error('Error fetching campaign data:', error);
    })
    .finally(() => {
      setTableLoading({ ...tableLoading, performance: false });
    });
  };

  // Sample 1 kartını Top Keywords kartına dönüştürelim
  function TopKeywordsCard() {
    // Örnek anahtar kelime verileri
    const keywords = [
      { keyword: "leather jacket", impressions: 24500, clicks: 2180, ctr: 8.9, cpc: 1.72, conversions: 95, cost: 3750 },
      { keyword: "leather bags", impressions: 19200, clicks: 1560, ctr: 8.1, cpc: 1.43, conversions: 72, cost: 2230 },
      { keyword: "handbags", impressions: 42800, clicks: 3120, ctr: 7.3, cpc: 1.95, conversions: 103, cost: 6084 },
      { keyword: "wallet", impressions: 31500, clicks: 2640, ctr: 8.4, cpc: 1.25, conversions: 118, cost: 3300 },
      { keyword: "leather belt", impressions: 16800, clicks: 1120, ctr: 6.7, cpc: 1.38, conversions: 43, cost: 1545 }
    ];

    return (
      <Card className="rounded-3xl overflow-hidden border border-gray-200/80">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Top Keywords</h2>
            <Select
              className="w-32"
              defaultValue="clicks"
              options={[
                { value: 'clicks', label: 'By Clicks' },
                { value: 'conversions', label: 'By Conversions' },
                { value: 'impressions', label: 'By Impressions' },
                { value: 'cost', label: 'By Cost' }
              ]}
            />
            </div>
            </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Impressions</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Clicks</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">CTR</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">CPC</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Conv.</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {keywords.map((keyword, index) => (
                <tr key={`keyword-${keyword.id || keyword.text || index}`} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-800">{keyword.keyword}</div>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">{keyword.impressions.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-medium text-blue-600">{keyword.clicks.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-gray-700">{keyword.ctr}%</td>
                  <td className="py-3 px-4 text-right text-gray-700">{keyword.cpc.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-medium text-green-600">{keyword.conversions}</td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'TRY',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                      currencyDisplay: 'code'
                    }).format(keyword.cost).replace('TRY', '').trim()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <div className="flex justify-end">
            <button className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
              View all keywords
              <ChevronRight className="w-4 h-4" />
            </button>
            </div>
            </div>
      </Card>
    );
  }

  return (
    <div className="p-6 space-y-8 overflow-x-hidden">
      {/* Header - Düzenlendi ve date filtreleri çalışır hale getirildi */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <img src="/google_ads-512.webp" alt="Google Ads" className="w-10 h-10 md:w-12 md:h-12" />
            <div>
            <h1 className="text-2xl font-semibold text-gray-800">Google Ads</h1>
            <p className="text-sm text-gray-500 mt-1">Campaign Performance Overview</p>
            </div>
          </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {gaAccountOptions.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-200/80 bg-white text-gray-700 shadow-sm min-w-[160px]"
            >
              <option value="all">Tüm Hesaplar</option>
              {gaAccountOptions.map(id => (
                <option key={id} value={id}>{gaAccountNameMap[id] || id}</option>
              ))}
            </select>
          )}
          {/* Unified date range filter — keeps fetchCampaignData() behavior intact */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
              // Only trigger an immediate refetch for preset selections. For 'custom',
              // fetch fires once both dates are picked (handled in the useMemo deps below).
              if (range !== 'custom') {
                fetchCampaignData(range);
              } else if (start && end) {
                fetchCampaignData(range);
              }
            }}
            variant="tabs"
            presets={['today', 'yesterday', '7days', '30days', 'this_month', 'lastMonth', 'all_time', 'custom']}
          />

          {showYatasCampaignFilter && (
            <select
              value={yatasCampaignFilter}
              onChange={(e) => setYatasCampaignFilter(e.target.value)}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-200/80 bg-white text-gray-700 shadow-sm"
              aria-label="Kampanya türü"
            >
              <option value="all">Tüm Kampanyalar</option>
              <option value="performance">Performans Kampanyaları</option>
              <option value="branding">Branding Kampanyaları</option>
            </select>
          )}

          {/* Custom date popover removed — <DateRangeFilter /> renders its own calendar */}

          {/* Refresh Button */}
          <button 
            onClick={handleRefresh}
            className="p-2 text-gray-600 hover:text-blue-600 bg-white border border-gray-200/80 rounded-2xl hover:bg-gray-50 transition-colors shadow-sm"
            title="Verileri yenile"
          >
            <RefreshCw className="w-5 h-5" />
          </button>

            </div>
            </div>

      <Ga4WebOnlyNote />

      {/* Budget Utilization & Forecasts - Daha kalın kenar ve artırılmış gölge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 min-w-0">
        {/* Daily Performance Line Chart */}
        <div className={`${enhancedCardClass} lg:col-span-2 p-4 bg-white min-w-0 overflow-hidden`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h3 className="text-sm font-medium text-gray-800">Daily Performance</h3>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-100 text-xs font-medium text-blue-700">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Cost ₺{formatNumber(dailyUnifiedPerformance?.totals?.cost ?? 0, { isCost: true })}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs font-medium text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Revenue ₺{formatNumber(dailyUnifiedPerformance?.totals?.ga4_total_revenue ?? 0, { isCost: true })}
              </span>
            </div>
          </div>

          <div className="h-[220px] md:h-[260px] -mx-1">
            {tableLoading.unifiedGaPerformance ? (
              <div className="loading-logo-container" style={{ minHeight: '220px' }}>
                <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 40, height: 40 }} />
              </div>
            ) : chartData.labels && chartData.labels.length > 0 ? (
              <LineChart
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: {
                    padding: { top: 4, right: 12, bottom: 4, left: 12 }
                  },
                  interaction: {
                    mode: 'index',
                    intersect: false
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      border: { display: false },
                      ticks: {
                        maxRotation: 0,
                        maxTicksLimit: 8,
                        font: { size: 10 },
                        color: '#94a3b8'
                      }
                    },
                    y: {
                      position: 'left',
                      beginAtZero: false,
                      border: { display: false },
                      ticks: {
                        callback: function(value) {
                          return '₺' + (value / 1000) + 'K';
                        },
                        font: { size: 10 },
                        color: '#10b981',
                        maxTicksLimit: 5
                      },
                      grid: {
                        color: 'rgba(16, 185, 129, 0.15)',
                        drawTicks: false
                      }
                    },
                    y1: {
                      position: 'right',
                      beginAtZero: false,
                      border: { display: false },
                      grid: { drawOnChartArea: false },
                      ticks: {
                        callback: function(value) {
                          return '₺' + (value / 1000) + 'K';
                        },
                        font: { size: 10 },
                        color: '#3b82f6',
                        maxTicksLimit: 5
                      }
                    }
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(255,255,255,0.98)',
                      titleColor: '#1e293b',
                      bodyColor: '#475569',
                      borderColor: '#e2e8f0',
                      borderWidth: 1,
                      padding: 10,
                      boxPadding: 4,
                      cornerRadius: 8,
                      titleFont: { size: 12, weight: '600' },
                      bodyFont: { size: 11 },
                      usePointStyle: true,
                      callbacks: {
                        title: function(tooltipItems) {
                          return tooltipItems[0].label;
                        },
                        label: function(context) {
                          const label = context.dataset.label || '';
                          const value = context.raw || 0;
                          return `${label}: ₺${formatNumber(value, { isCost: true })}`;
                        }
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <BarChart3 className="w-12 h-12 text-gray-300 mb-2" />
                <h3 className="text-base font-medium text-gray-600 mb-1">No data available</h3>
                <p className="text-sm text-gray-400 text-center max-w-xs">
                  No cost data for the selected date range.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Budget Utilization Card */}
        <div className={`${enhancedCardClass} p-4 bg-white min-w-0 flex flex-col min-h-[280px]`}>
          <div className="flex justify-between items-center mb-3 flex-shrink-0">
            <h3 className="text-sm font-medium">Budget Utilization</h3>
            <div className="p-1.5 bg-blue-50 rounded-full border border-blue-100">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
          </div>

          {tableLoading.dailySummary ? (
            <div className="loading-logo-container flex-1" style={{ minHeight: 'auto' }}>
              <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 40, height: 40 }} />
            </div>
          ) : dailySummaryData && dailySummaryData.length > 0 ? (
            (() => {
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonth = today.getMonth();
              const currentDay = today.getDate();
              const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
              const remainingDays = totalDaysInMonth - currentDay;
              const totalSpent = dailySummaryData.reduce((sum, item) => {
                const d = new Date(item.date);
                return (d.getFullYear() === currentYear && d.getMonth() === currentMonth) ? sum + item.totalCost : sum;
              }, 0);
              const hasTarget = targetBudget != null && Number(targetBudget) > 0;
              const spentPct = hasTarget ? Math.min(Math.round((totalSpent / Number(targetBudget)) * 100), 100) : 0;
              const remainingBudget = hasTarget ? Math.max(0, Number(targetBudget) - totalSpent) : 0;
              const dailyTarget = hasTarget && remainingDays > 0 ? remainingBudget / remainingDays : 0;
              const ringColor = !hasTarget ? '#9ca3af' : spentPct > 80 ? '#ef4444' : spentPct > 50 ? '#f59e0b' : '#3b82f6';
              const circumference = 2 * Math.PI * 36;
              const strokeDash = (spentPct / 100) * circumference;

              return (
                <div className="flex-1 flex flex-col justify-between gap-4">
                  {/* Top: Spend + Ring */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 84 84">
                        <circle cx="42" cy="42" r="36" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                        <circle cx="42" cy="42" r="36" fill="none" stroke={ringColor} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash} className="transition-all duration-500" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums" style={{ color: ringColor }}>{spentPct}%</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">{today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
                      <p className="text-2xl font-bold tabular-nums text-gray-900 mt-0.5">₺{formatNumber(totalSpent, { isCost: true })}</p>
                      <div className="text-[11px] text-gray-500 mt-1">
                        {isEditingBudget || !hasTarget ? (
                          <span className="flex items-center gap-1.5">
                            <span className="text-base font-semibold text-gray-700">₺</span>
                            <input
                              type="text"
                              className="w-40 border border-blue-300 rounded-md px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 placeholder-gray-400"
                              defaultValue={hasTarget ? formatNumber(Number(targetBudget), { isCost: true }) : ''}
                              placeholder={hasTarget ? '' : 'Hedef bütçe gir'}
                              disabled={savingTargetBudget}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  e.target.dataset.cancelled = '1';
                                  setIsEditingBudget(false);
                                }
                              }}
                              onBlur={async (e) => {
                                if (e.target.dataset.cancelled === '1') {
                                  setIsEditingBudget(false);
                                  return;
                                }
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                const numValue = parseInt(value, 10);
                                if (!isNaN(numValue) && numValue > 0 && numValue !== Number(targetBudget)) {
                                  await saveMonthlyTarget(numValue);
                                }
                                setIsEditingBudget(false);
                              }}
                              autoFocus={isEditingBudget}
                            />
                            {!hasTarget && (
                              <span className="text-[10px] text-gray-400 italic">Enter ile kaydet</span>
                            )}
                          </span>
                        ) : (
                          <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setIsEditingBudget(true)}>
                            of ₺{formatNumber(Number(targetBudget), { isCost: true })} target
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="flex-shrink-0">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${spentPct}%`, backgroundColor: ringColor }}
                      />
                    </div>
                  </div>

                  {/* Bottom: 2-column metrics */}
                  <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                    <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                      <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Daily target</p>
                      <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">₺{formatNumber(dailyTarget, { isCost: true })}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                      <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Remaining</p>
                      <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">₺{formatNumber(remainingBudget, { isCost: true })}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{remainingDays} days left</p>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="flex flex-col items-center justify-center flex-1">
              <DollarSign className="w-12 h-12 text-gray-400 mb-2" />
              <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No budget data found for the selected date range.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Alt Bölüm Kartları - Daha kalın kenar ve artırılmış gölge */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 min-w-0 items-stretch">
        {/* Google Ads Performance */}
        <div className={`${enhancedCardClass} bg-white min-w-0 flex flex-col`}>
            {tableLoading.unifiedGaPerformance ? (
              <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
                <div className="flex flex-col items-center gap-3">
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 40, height: 40 }} />
                  <p className="text-sm text-gray-500">Loading performance data...</p>
                </div>
              </div>
            ) : dailyUnifiedPerformance.daily && dailyUnifiedPerformance.daily.length > 0 ? (
              <div className="flex flex-col overflow-hidden flex-1 min-h-0">
                {/* Sticky header */}
                <div className="overflow-x-auto flex-shrink-0">
                  <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <th className="text-left py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200">Date</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200">Ads Cost</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200">Clicks</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200">Ads Rev.</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200">GA4 Rev.</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase">CPC</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                {/* Scrollable body — max 7 rows visible */}
                <div className="overflow-y-auto flex-1 min-h-0 max-h-[245px]">
                  <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                    </colgroup>
                    <tbody className="divide-y divide-gray-50">
                      {dailyUnifiedPerformance.daily.map((day, index) => (
                        <tr key={`daily-${day.date || index}`} className="hover:bg-gray-50/50">
                          <td className="py-2 px-3 text-[11px] tabular-nums font-medium text-gray-800 border-r border-dashed border-gray-200">
                            {new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </td>
                          <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(day.cost)}</td>
                          <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200">{formatNumberCompactShort(day.clicks)}</td>
                          <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(day.conversion_value)}</td>
                          <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(day.ga4_total_revenue)}</td>
                          <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-700 font-medium">{formatCurrencyFixed2(day.clicks > 0 ? day.cost / day.clicks : 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Sticky total footer */}
                <div className="overflow-x-auto flex-shrink-0 border-t-2 border-gray-200 bg-gray-50/80">
                  <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                    </colgroup>
                    <tbody>
                      <tr className="font-medium">
                        <td className="py-2.5 px-3 border-r border-dashed border-gray-200">
                          <span className="text-[11px] font-semibold text-gray-800">Total</span>
                        </td>
                        <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(dailyUnifiedPerformance.totals.cost)}</td>
                        <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200">{formatNumberCompactShort(dailyUnifiedPerformance.totals.clicks)}</td>
                        <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(dailyUnifiedPerformance.totals.conversion_value)}</td>
                        <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200">{formatCurrencyCompactShort(dailyUnifiedPerformance.totals.ga4_total_revenue)}</td>
                        <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-bold text-gray-900">{formatCurrencyFixed2(dailyUnifiedPerformance.totals.clicks > 0 ? dailyUnifiedPerformance.totals.cost / dailyUnifiedPerformance.totals.clicks : 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <BarChart3 className="w-12 h-12 text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No performance data found for the selected date range.
                </p>
              </div>
            )}
        </div>

        {/* Performance Breakdown */}
        <div className={`${enhancedCardClass} md:col-span-2 bg-white min-w-0 flex flex-col`}>
            {tableLoading.unifiedGaPerformance ? (
              <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
                <div className="flex flex-col items-center gap-3">
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 40, height: 40 }} />
                  <p className="text-sm text-gray-500">Loading campaign data...</p>
                </div>
              </div>
            ) : campaignPerformanceData.length > 0 ? (
              <div className="flex flex-col overflow-hidden flex-1 min-h-0">
                {/* Frozen header */}
                <div className="overflow-x-auto flex-shrink-0">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase w-48 min-w-[160px] border-r border-dashed border-gray-200">Campaign Type</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">Ads Impr.</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">Ads Clicks</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">Ads Cost</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">Ads Trans.</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">Ads Rev.</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">ROAS</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">CPC</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-blue-50">CPS</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-orange-100">Sessions</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase border-r border-dashed border-gray-200 bg-orange-100">GA4 Trans.</th>
                        <th className="text-center py-2.5 px-3 text-[10px] font-medium text-gray-500 uppercase bg-orange-100">GA4 Rev.</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                {/* Scrollable body */}
                <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
                  <table className="w-full table-fixed">
                    <tbody>
                      {campaignPerformanceData
                        .filter(item => item.campaign_type !== 'TOTAL')
                        .map((item, index) => {
                          const typeInfo = campaignTypes[item.campaign_type] || {
                            icon: <HelpCircle className="w-3 h-3" />,
                            color: 'gray',
                            label: item.campaign_type
                          };
                          return (
                            <tr key={`campaign-${item.campaign_type}-${index}`} className="hover:bg-gray-50/50 border-b border-gray-50">
                              <td className="py-2 px-3 w-48 max-w-[200px] border-r border-dashed border-gray-200">
                                <div className="flex items-center gap-1.5 overflow-hidden">
                                  <div className={`p-1 rounded-md bg-${typeInfo.color}-100 flex-shrink-0`}>
                                    {(() => {
                                      const lowerSource = item.campaign_type.toLowerCase();
                                      if (lowerSource.includes('video')) return <img src="/video.svg" alt="Video" className="w-3 h-3" />;
                                      if (lowerSource.includes('shop')) return <img src="/shopping.svg" alt="Shopping" className="w-3 h-3" />;
                                      if (lowerSource.includes('search')) return <img src="/search.svg" alt="Search" className="w-3 h-3" />;
                                      if (lowerSource.includes('performance') || lowerSource.includes('pmax')) return <img src="/performancemax.svg" alt="PMax" className="w-3 h-3" />;
                                      if (lowerSource.includes('display')) return <img src="/display.svg" alt="Display" className="w-3 h-3" />;
                                      if (lowerSource.includes('demand')) return <img src="/demand.svg" alt="Demand Gen" className="w-3 h-3" />;
                                      return <img src="/search.svg" alt={item.campaign_type} className="w-3 h-3" />;
                                    })()}
                                  </div>
                                  <span className="text-[11px] font-medium text-gray-800 truncate" title={typeInfo.label}>{typeInfo.label}</span>
                                  {item.campaignCount > 0 && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 whitespace-nowrap flex-shrink-0">
                                      {item.campaignCount} campaigns
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-blue-50">{formatNumber(item.impressions)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-blue-50">{formatNumber(item.clicks)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyCompactShortDecimals(item.cost)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-blue-50">{Math.round(Number(item.conversions || 0))}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-blue-50">₺{formatNumber(item.conversion_value)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-800 font-semibold border-r border-dashed border-gray-200 bg-blue-50">{item.roas.toFixed(2)}x</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-700 font-medium border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyFixed2(item.cpc)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-700 font-medium border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyFixed2(item.cps)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-orange-100">{formatNumber(item.ga4_sessions)}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 border-r border-dashed border-gray-200 bg-orange-100">{Math.round(Number(item.ga4_purchases || 0))}</td>
                              <td className="text-right py-2 px-3 text-[11px] tabular-nums text-gray-600 bg-orange-100">₺{formatNumber(item.ga4_total_revenue)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                {/* Frozen total footer */}
                {(() => {
                  const total = campaignPerformanceData.find(item => item.campaign_type === 'TOTAL');
                  if (!total) return null;
                  return (
                    <div className="overflow-x-auto flex-shrink-0 border-t-2 border-gray-200 bg-gray-50/80">
                      <table className="w-full table-fixed">
                        <tbody>
                          <tr className="font-medium">
                            <td className="py-2.5 px-3 w-48 min-w-[160px] border-r border-dashed border-gray-200">
                              <div className="flex items-center gap-1.5">
                                <div className="p-1 rounded-md bg-gray-200 flex-shrink-0">
                                  <BarChart3 className="w-3 h-3 text-gray-600" />
                                </div>
                                <span className="text-[11px] font-semibold text-gray-800">Total</span>
                                {total.campaignCount > 0 && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
                                    {total.campaignCount} campaigns
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{formatNumber(total.impressions)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{formatNumber(total.clicks)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyCompactShortDecimals(total.cost)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{Math.round(Number(total.conversions || 0))}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">₺{formatNumber(total.conversion_value)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-bold text-gray-900 border-r border-dashed border-gray-200 bg-blue-50">{(total.roas || 0).toFixed(2)}x</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyFixed2(total.cpc)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-blue-50">{formatCurrencyFixed2(total.cps)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-orange-100">{formatNumber(total.ga4_sessions)}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 border-r border-dashed border-gray-200 bg-orange-100">{Math.round(Number(total.ga4_purchases || 0))}</td>
                            <td className="text-right py-2.5 px-3 text-[11px] tabular-nums font-semibold text-gray-800 bg-orange-100">₺{formatNumber(total.ga4_total_revenue)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <BarChart3 className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">No campaign data for this period</p>
              </div>
            )}
        </div>
      </div>

      {/* Best Campaign Type yerine yeni komponentleri yerleştirdim */}
      <div className="grid grid-cols-2 gap-6 min-w-0">
        {/* Sol taraf - 2x2 grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Brand Impression Share */}
          <div className={`${enhancedCardClass} bg-white min-w-0`}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-gray-700">Search Impression Share</h3>
                <div className="p-1.5 rounded-lg bg-blue-50">
                  <PieChartIcon className="w-4 h-4 text-blue-500" />
                </div>
              </div>
              {tableLoading.impressionShare ? (
                <div className="loading-logo-container py-8" style={{ minHeight: 'auto' }}>
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 32, height: 32 }} />
                </div>
              ) : impressionShareData.length > 0 ? (
                <div className="overflow-y-auto max-h-60">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-1.5 pr-2 text-[10px] font-medium text-gray-400 uppercase">Campaign</th>
                        <th className="text-right py-1.5 px-1 text-[10px] font-medium text-gray-400 uppercase">Top IS</th>
                        <th className="text-right py-1.5 px-1 text-[10px] font-medium text-gray-400 uppercase whitespace-nowrap">Lost (Rank)</th>
                        <th className="text-right py-1.5 pl-1 text-[10px] font-medium text-gray-400 uppercase whitespace-nowrap">Lost (Budg.)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {impressionShareData
                        .filter(item => item.search_top_impression_share > 0 || item.search_rank_lost_impression_share > 0 || item.search_budget_lost_impression_share > 0)
                        .map((item, index) => (
                          <tr key={`impression-${item.campaign_id || item.campaign_name || index}`} className="hover:bg-gray-50">
                            <td className="py-1.5 pr-2 text-[11px] font-medium text-gray-700 max-w-[120px] truncate" title={item.campaign_name}>
                              {item.campaign_name}
                            </td>
                            <td className="py-1.5 px-1 text-right text-[11px] font-semibold text-green-600 tabular-nums">
                              {(item.search_top_impression_share * 100).toFixed(0)}%
                            </td>
                            <td className="py-1.5 px-1 text-right text-[11px] text-red-500 tabular-nums">
                              {(item.search_rank_lost_impression_share * 100).toFixed(0)}%
                            </td>
                            <td className="py-1.5 pl-1 text-right text-[11px] text-orange-500 tabular-nums">
                              {(item.search_budget_lost_impression_share * 100).toFixed(0)}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <PieChartIcon className="w-7 h-7 text-gray-300 mb-1.5" />
                  <p className="text-xs text-gray-400">No data</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Keywords */}
          <div className={`${enhancedCardClass} bg-white min-w-0`}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-gray-700">Top Keywords</h3>
                <div className="p-1.5 rounded-lg bg-purple-50">
                  <Search className="w-4 h-4 text-purple-500" />
                </div>
              </div>
              {tableLoading.topKeywords ? (
                <div className="loading-logo-container py-8" style={{ minHeight: 'auto' }}>
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 32, height: 32 }} />
                </div>
              ) : processedTopKeywords.length > 0 ? (
                <div className="overflow-y-auto max-h-60">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-1.5 pr-2 text-[10px] font-medium text-gray-400 uppercase">Keyword</th>
                        <th className="text-right py-1.5 px-1 text-[10px] font-medium text-gray-400 uppercase">Cost</th>
                        <th className="text-right py-1.5 px-1 text-[10px] font-medium text-gray-400 uppercase">Clicks</th>
                        <th className="text-right py-1.5 pl-1 text-[10px] font-medium text-gray-400 uppercase">Conv.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {processedTopKeywords.map((keyword, index) => (
                        <tr key={`top-keyword-${keyword.keyword || index}`} className="hover:bg-gray-50">
                          <td className="py-1.5 pr-2 text-[11px] font-medium text-gray-700 max-w-[130px] truncate" title={keyword.keyword}>
                            {keyword.keyword}
                          </td>
                          <td className="py-1.5 px-1 text-right text-[11px] text-gray-600 tabular-nums">{formatCurrencyCompactShort(keyword.cost)}</td>
                          <td className="py-1.5 px-1 text-right text-[11px] font-medium text-blue-600 tabular-nums">{formatNumberCompactShort(keyword.clicks)}</td>
                          <td className="py-1.5 pl-1 text-right text-[11px] font-medium text-green-600 tabular-nums">{formatNumberCompactShort(keyword.conversions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <Search className="w-7 h-7 text-gray-300 mb-1.5" />
                  <p className="text-xs text-gray-400">No data</p>
                </div>
              )}
            </div>
          </div>

          {/* Auction Insights */}
          <div className={`${enhancedCardClass} bg-white min-w-0`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700">Conversion Actions</h3>
                <div className="p-2 rounded-lg bg-green-100">
                  <Target className="w-5 h-5 text-green-600" />
                </div>
              </div>
              {tableLoading.conversionActions ? (
                <div className="loading-logo-container py-8" style={{ minHeight: 'auto' }}>
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 36, height: 36 }} />
                </div>
              ) : processedConversionActions.length > 0 ? (
                 <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                     {/* Header */}
                      <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-gray-500 pb-1 border-b">
                          <div className="col-span-1">Action Name</div>
                          <div className="text-right">Conversions</div>
                          <div className="text-right">Value</div>
                      </div>
                      {processedConversionActions.map((action, index) => (
                          <div key={`conversion-${action.id || action.name || index}`} className="grid grid-cols-3 gap-2 items-center text-xs p-1.5 rounded-md hover:bg-gray-50">
                              <div className="col-span-1 font-medium text-gray-800 truncate" title={action.name}>
                                  {action.name}
                              </div>
                              <div className="text-right font-semibold text-gray-800">
                                  {formatNumber(action.conversions, {decimals: 0})}
                              </div>
                              <div className="text-right text-gray-700">
                                  ₺{formatNumber(action.value, {isCost: true})}
                              </div>
                          </div>
                      ))}
                 </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="text-gray-400 mb-2">
                    <Target className="w-8 h-8 mx-auto mb-2" />
                  </div>
                  <p className="text-sm text-gray-500">No Conversion data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Device Performance */}
          <div className={`${enhancedCardClass} bg-white`}>
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-700">Device Performance</h3>
                <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-0.5">
                  {['cost', 'conversionValue', 'conversions'].map(metric => (
                    <button
                      key={`device-metric-${metric}`}
                      onClick={() => setActiveDeviceMetric(metric)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                        metric === activeDeviceMetric 
                          ? "bg-white text-gray-800 shadow-sm" 
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {metric === 'conversionValue' ? 'Revenue' : metric.charAt(0).toUpperCase() + metric.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {tableLoading.devicePerformance ? (
                <div className="loading-logo-container py-10" style={{ minHeight: 'auto' }}>
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 36, height: 36 }} />
                </div>
              ) : devicePerformanceData.length > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="h-36 w-full max-w-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={devicePerformanceData}
                          dataKey={activeDeviceMetric}
                          nameKey="deviceType"
                          cx="50%"
                          cy="50%"
                          innerRadius={42}
                          outerRadius={62}
                          fill="#8884d8"
                          paddingAngle={4}
                          strokeWidth={0}
                        >
                          {devicePerformanceData.map((entry, index) => (
                            <Cell key={`device-cell-${entry.device || index}`} fill={['#3b82f6', '#10b981', '#f97316', '#6b21a8', '#9ca3af'][index % 5]} />
                          ))}
                        </Pie>
                        <TooltipRecharts formatter={(value) => {
                          if (activeDeviceMetric === 'cost' || activeDeviceMetric === 'conversionValue') {
                            return `₺${formatNumber(value, { isCost: true })}`;
                          }
                          return formatNumber(value, { decimals: 0 });
                        }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full mt-3 space-y-1.5">
                    {(() => {
                      const totalValue = devicePerformanceData.reduce((sum, entry) => sum + (entry[activeDeviceMetric] || 0), 0);
                      const colors = ['#3b82f6', '#10b981', '#f97316', '#6b21a8', '#9ca3af'];
                      return devicePerformanceData.map((entry, index) => {
                        const pct = totalValue > 0 ? ((entry[activeDeviceMetric] / totalValue) * 100) : 0;
                        return (
                          <div key={`device-legend-${entry.device || index}`} className="flex items-center gap-2.5">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[index % 5] }} />
                            <span className="text-xs text-gray-600 w-24 truncate">{formatDeviceName(entry.deviceType)}</span>
                            <span className="text-xs font-semibold text-gray-800 ml-auto tabular-nums">
                              {activeDeviceMetric === 'cost' || activeDeviceMetric === 'conversionValue'
                                ? formatCurrencyCompactShort(entry[activeDeviceMetric])
                                : formatNumberCompactShort(entry[activeDeviceMetric])}
                            </span>
                            <span className="text-[10px] text-gray-400 w-12 text-right tabular-nums">({pct.toFixed(1)}%)</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10">
                  <Smartphone className="w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-xs text-gray-400">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sağ taraf - Chart */}
        <Card className="rounded-3xl overflow-hidden border border-gray-200/80 min-w-0">
          <div className="p-6 border-b">
            <h2 className="text-lg font-medium">Top 10 Campaigns by Cost</h2>
            <p className="text-sm text-gray-500 mt-1">Performance of top campaigns for the selected period</p>
          </div>
          <div className="p-6">
            {tableLoading.unifiedGaPerformance ? (
              <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
                <div className="flex flex-col items-center gap-4">
                  <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 48, height: 48 }} />
                  <h3 className="text-lg font-medium text-gray-700 mb-1">Loading table data...</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Please wait while we prepare the top campaigns table.
                  </p>
                </div>
              </div>
            ) : topCampaignsData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-left w-48 min-w-[160px] text-[11px]">Campaign</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">Cost</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">Clicks</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">CPC</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">Ads Revenue</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">GA4 Revenue</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-right text-[11px]">Ads ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topCampaignsData.map((campaign, index) => (
                      <tr key={`top-campaign-${campaign.id || campaign.name || index}`} className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-800 text-xs max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis" title={campaign.name}>
                          {campaign.name}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatCurrencyCompactShort(campaign.cost)}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatNumberCompactShort(campaign.clicks)}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatCurrencyFixed2(campaign.cpc)}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatCurrencyCompactShort(campaign.conversion_value)}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatCurrencyCompactShort(campaign.ga4_total_revenue)}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-800">{campaign.roas.toFixed(2)}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="text-gray-400 mb-2">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-1">No campaign data</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No campaign data found for the selected date range.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Campaign Performance Trends - Statik SVG chart ile */}
      <Card className="rounded-3xl overflow-hidden border border-gray-200/80 min-w-0">
        <div className="p-6 border-b">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-3 lg:space-y-0">
            <div>
              <h2 className="text-lg font-medium">Campaign Performance Trends</h2>
              <p className="text-sm text-gray-500 mt-1">
                Monthly {activeMetric} by Campaign Type
              </p>
            </div>
            
            {/* Metrik seçenekleri */}
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
              {["Cost", "Revenue", "Cost+Revenue", "Clicks", "Impressions", "Conversions", "Sessions", "CPC", "CPS", "ROAS"].map((metric) => (
                <button
                  key={`pivot-metric-${metric}`}
                  onClick={() => setActiveMetric(metric)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    metric === activeMetric 
                      ? "bg-blue-600 text-white" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {metric}
                </button>
              ))}
            </div>
            </div>
            </div>
        <div className="p-0">
          {tableLoading.performance ? (
            <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
              <div className="flex flex-col items-center gap-4">
                <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 48, height: 48 }} />
                <h3 className="text-lg font-medium text-gray-700 mb-1">Loading chart data...</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  Please wait while we prepare the performance chart.
                </p>
              </div>
            </div>
          ) : monthlyCampaignPerformanceData.datasets.length > 0 ? (
            <div className="overflow-x-auto overflow-y-auto max-h-[420px] w-full relative isolate">
              <table className="text-sm min-w-max">
                <thead className="relative z-[100]">
                  <tr className="bg-gray-50">
                    {performancePivotTableData.headers.map((header, index) => (
                      <th key={`pivot-header-${header}-${index}`} className={`py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[11px] border-r border-dashed border-gray-200 sticky top-0 z-[100] bg-gray-50 ${index === 0 ? 'text-left sticky left-0 min-w-[180px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'text-right min-w-[120px]'} ${index === performancePivotTableData.headers.length - 1 ? 'last:border-0 sticky right-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''}`}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
              {performancePivotTableData.rows.map((row, rowIndex) => (
                    <React.Fragment key={`pivot-row-${row.campaignType}-${rowIndex}`}>
                      <tr className="group hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded14dTypes(prev => ({...prev, [row.campaignType]: !prev[row.campaignType]}))}>
                        <td className="sticky left-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 font-medium text-gray-800 min-w-[180px] whitespace-normal break-words leading-snug border-r border-dashed border-gray-200" title={campaignTypes[row.campaignType]?.label || row.campaignType}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">{expanded14dTypes[row.campaignType] ? '▼' : '▶'}</span>
                            {campaignTypes[row.campaignType]?.label || row.campaignType}
                          </div>
                        </td>
                        {row.values.map((value, colIndex) => (
                          <td key={`pivot-cell-${rowIndex}-${colIndex}`} className="py-3 px-4 text-right text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                        {['CPC','CPS'].includes(activeMetric) && typeof value !== 'object' && formatCurrencyFixed2(value)}
                            {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(value) : formatNumberCompactShort(value))}
                            {activeMetric === 'Cost+Revenue' && (
                              <div className="flex items-center justify-end gap-2">
                                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(value.cost)}</span>
                                <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(value.revenue)}</span>
                              </div>
                            )}
                            {activeMetric === 'ROAS' ? 'x' : ''}
                            {['Cost', 'Revenue', 'Clicks', 'Impressions', 'Conversions'].includes(activeMetric) && (
                              <span className="text-gray-400 text-xs ml-1">
                                ({((value / performancePivotTableData.totals[colIndex]) * 100 || 0).toFixed(1)}%)
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="sticky right-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 text-right font-bold text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      {['CPC','CPS'].includes(activeMetric) && typeof row.total !== 'object' && formatCurrencyFixed2(row.total)}
                          {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(row.total) : formatNumberCompactShort(row.total))}
                          {activeMetric === 'Cost+Revenue' && (
                            <div className="flex items-center justify-end gap-2">
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort(row.total.cost)}</span>
                              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort(row.total.revenue)}</span>
                            </div>
                          )}
                          {activeMetric === 'ROAS' ? 'x' : ''}
                          {['Cost', 'Revenue', 'Clicks', 'Impressions', 'Conversions'].includes(activeMetric) && (
                            <span className="text-gray-400 text-xs ml-1">
                              ({((row.total / performancePivotTableData.totals[performancePivotTableData.totals.length - 1]) * 100 || 0).toFixed(1)}%)
                            </span>
                          )}
                        </td>
                      </tr>
                      {expanded14dTypes[row.campaignType] && row.children && row.children.length > 0 && (
                        row.children.map((child, ci) => (
                          <tr key={`pivot-row-${row.campaignType}-child-${ci}`} className="bg-gray-50">
                            <td className="sticky left-0 z-[5] bg-gray-50 py-2 px-4 text-xs text-gray-700 min-w-[180px] pr-2 border-r border-dashed border-gray-200">
                              <div className="pl-6 whitespace-normal break-words leading-snug">{child.name}</div>
                            </td>
                            {child.values.map((v, vi) => (
                              <td key={`child-val-month-${ci}-${vi}`} className="py-2 px-4 text-right text-xs text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                                {['CPC','CPS'].includes(activeMetric) && typeof v !== 'object' && formatCurrencyFixed2(v)}
                                {activeMetric !== 'CPC' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(v) : formatNumberCompactShort(v))}
                                {activeMetric === 'Cost+Revenue' && (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="px-1 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(v.cost)}</span>
                                    <span className="px-1 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(v.revenue)}</span>
                                  </div>
                                )}
                                {activeMetric === 'ROAS' ? 'x' : ''}
                              </td>
                            ))}
                            <td className="sticky right-0 z-[5] bg-gray-50 py-2 px-4 text-right text-xs font-medium text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                              {['CPC','CPS'].includes(activeMetric) && typeof child.total !== 'object' && formatCurrencyFixed2(child.total)}
                              {activeMetric !== 'CPC' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(child.total) : formatNumberCompactShort(child.total))}
                              {activeMetric === 'Cost+Revenue' && (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px]">{formatCurrencyCompactShort(child.total.cost)}</span>
                                  <span className="px-1 py-0.5 rounded bg-green-100 text-green-700 text-[10px]">{formatCurrencyCompactShort(child.total.revenue)}</span>
                                </div>
                              )}
                              {activeMetric === 'ROAS' ? 'x' : ''}
                            </td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="relative z-[100]">
                  <tr className="bg-gray-100 font-bold text-gray-800">
                    <td className="sticky left-0 bottom-0 z-[100] bg-gray-100 py-3 px-4 text-left min-w-[180px] border-r border-dashed border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">Total</td>
                    {performancePivotTableData.totals.map((total, index) => (
                      <td key={index} className={`py-3 px-4 text-right border-r border-dashed border-gray-200 last:border-0 min-w-[120px] z-[100] bg-gray-100 ${index === performancePivotTableData.totals.length - 1 ? 'sticky right-0 bottom-0 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'sticky bottom-0'}`}>
                        {['CPC','CPS'].includes(activeMetric) && typeof total !== 'object' && formatCurrencyFixed2(total)}
                        {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(total) : formatNumberCompactShort(total))}
                        {activeMetric === 'Cost+Revenue' && (
                          <div className="flex items-center justify-end gap-2">
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort(total.cost)}</span>
                            <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort(total.revenue)}</span>
                          </div>
                        )}
                        {activeMetric === 'ROAS' ? 'x' : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-gray-400 mb-2">
                <BarChart3 className="w-12 h-12 mx-auto mb-2" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No performance data found for the selected date range.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Campaign Performance Trends - Copy (Fixed Last 30 Days) */}
      <Card className="rounded-3xl overflow-hidden border border-gray-200/80 min-w-0">
        <div className="p-6 border-b">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-3 lg:space-y-0">
            <div>
              <h2 className="text-lg font-medium">Campaign Performance Trends</h2>
              <p className="text-sm text-gray-500 mt-1">Last 10 days (yesterday back) — {activeMetric} by Campaign Type</p>
            </div>
            
            {/* Metrik seçenekleri */}
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
              {["Cost", "Revenue", "Cost+Revenue", "Clicks", "Impressions", "Conversions", "Sessions", "CPC", "CPS", "ROAS"].map((metric) => (
                <button
                  key={`pivot-metric-copy-${metric}`}
                  onClick={() => setActiveMetric(metric)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    metric === activeMetric 
                      ? "bg-blue-600 text-white" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {metric}
                </button>
              ))}
            </div>
          </div>
          </div>
        <div className="p-0">
          {tableLoading.unifiedGaPerformance ? (
            <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
              <div className="flex flex-col items-center gap-4">
                <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 48, height: 48 }} />
                <h3 className="text-lg font-medium text-gray-700 mb-1">Loading chart data...</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  Please wait while we prepare the performance chart.
                </p>
              </div>
            </div>
          ) : last30PivotTableData.headers.length > 0 ? (
            <div className="overflow-x-auto overflow-y-auto max-h-[420px] w-full relative isolate">
              <table className="text-sm min-w-max">
                <thead className="relative z-[100]">
                  <tr className="bg-gray-50">
                    {last30PivotTableData.headers.map((header, index) => (
                      <th key={`pivot-header-copy-${header}-${index}`} className={`py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[11px] border-r border-dashed border-gray-200 sticky top-0 z-[100] bg-gray-50 ${index === 0 ? 'text-left sticky left-0 min-w-[180px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'text-right min-w-[120px]'} ${index === last30PivotTableData.headers.length - 1 ? 'last:border-0 sticky right-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''}`}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {last30PivotTableData.rows.map((row, rowIndex) => (
                    <React.Fragment key={`pivot-row-copy-${row.campaignType}-${rowIndex}`}>
                      <tr className="group hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedMonthlyTypes(prev => ({...prev, [row.campaignType]: !prev[row.campaignType]}))}>
                        <td className="sticky left-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 font-medium text-gray-800 min-w-[180px] whitespace-normal break-words leading-snug border-r border-dashed border-gray-200" title={campaignTypes[row.campaignType]?.label || row.campaignType}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">{expandedMonthlyTypes[row.campaignType] ? '▼' : '▶'}</span>
                            {campaignTypes[row.campaignType]?.label || row.campaignType}
                          </div>
                        </td>
                            {row.values.map((value, colIndex) => (
                          <td key={`pivot-cell-copy-${rowIndex}-${colIndex}`} className="py-3 px-4 text-right text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                            {['CPC','CPS'].includes(activeMetric) && typeof value !== 'object' && formatCurrencyFixed2(value)}
                            {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(value) : formatNumberCompactShort(value))}
                            {activeMetric === 'Cost+Revenue' && typeof value === 'object' && (
                              <div className="flex items-center justify-end gap-2">
                                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(value.cost)}</span>
                                <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(value.revenue)}</span>
                              </div>
                            )}
                            {activeMetric === 'ROAS' ? 'x' : ''}
                          </td>
                        ))}
                        <td className="sticky right-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 text-right font-bold text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                          {['CPC','CPS'].includes(activeMetric) && typeof row.total !== 'object' && formatCurrencyFixed2(row.total)}
                          {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(row.total) : formatNumberCompactShort(row.total))}
                          {activeMetric === 'Cost+Revenue' && (
                            <div className="flex items-center justify-end gap-2">
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof row.total === 'object' ? row.total.cost : 0))}</span>
                              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof row.total === 'object' ? row.total.revenue : 0))}</span>
                            </div>
                          )}
                          {activeMetric === 'ROAS' ? 'x' : ''}
                        </td>
                      </tr>
                      {expandedMonthlyTypes[row.campaignType] && row.children && row.children.length > 0 && (
                        row.children.map((child, ci) => (
                          <tr key={`pivot-row-copy-${row.campaignType}-child-${ci}`} className="bg-gray-50">
                            <td className="sticky left-0 z-[5] bg-gray-50 py-2 px-4 text-xs text-gray-700 min-w-[180px] pr-2 border-r border-dashed border-gray-200">
                              <div className="pl-6 whitespace-normal break-words leading-snug">{child.name}</div>
                            </td>
                            {child.values.map((v, vi) => (
                              <td key={`child-val-${ci}-${vi}`} className="py-2 px-4 text-right text-xs text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                                {['CPC','CPS'].includes(activeMetric) && typeof v !== 'object' && formatCurrencyFixed2(v)}
                                {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(v) : formatNumberCompactShort(v))}
                                {activeMetric === 'Cost+Revenue' && typeof v === 'object' && (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="px-1 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(v.cost)}</span>
                                    <span className="px-1 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(v.revenue)}</span>
                                  </div>
                                )}
                                {activeMetric === 'ROAS' ? 'x' : ''}
                              </td>
                            ))}
                            <td className="sticky right-0 z-[5] bg-gray-50 py-2 px-4 text-right text-xs font-medium text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                              {['CPC','CPS'].includes(activeMetric) && typeof child.total !== 'object' && formatCurrencyFixed2(child.total)}
                              {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(child.total) : formatNumberCompactShort(child.total))}
                              {activeMetric === 'Cost+Revenue' && (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px]">{formatCurrencyCompactShort((typeof child.total === 'object' ? child.total.cost : 0))}</span>
                                  <span className="px-1 py-0.5 rounded bg-green-100 text-green-700 text-[10px]">{formatCurrencyCompactShort((typeof child.total === 'object' ? child.total.revenue : 0))}</span>
                                </div>
                              )}
                              {activeMetric === 'ROAS' ? 'x' : ''}
                            </td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="relative z-[100]">
                  <tr className="bg-gray-100 font-bold text-gray-800">
                    <td className="sticky left-0 bottom-0 z-[100] bg-gray-100 py-3 px-4 text-left min-w-[180px] border-r border-dashed border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">Total</td>
                    {last30PivotTableData.totals.map((total, index) => (
                      <td key={`pivot-total-copy-${index}`} className={`py-3 px-4 text-right border-r border-dashed border-gray-200 last:border-0 min-w-[120px] z-[100] bg-gray-100 ${index === last30PivotTableData.totals.length - 1 ? 'sticky right-0 bottom-0 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'sticky bottom-0'}`}>
                        {['CPC','CPS'].includes(activeMetric) && typeof total !== 'object' && formatCurrencyFixed2(total)}
                        {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(total) : formatNumberCompactShort(total))}
                        {activeMetric === 'Cost+Revenue' && (
                          <div className="flex items-center justify-end gap-2">
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof total === 'object' ? total.cost : 0))}</span>
                            <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof total === 'object' ? total.revenue : 0))}</span>
                          </div>
                        )}
                        {activeMetric === 'ROAS' ? 'x' : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-gray-400 mb-2">
                <BarChart3 className="w-12 h-12 mx-auto mb-2" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No performance data found for the selected date range.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Campaign Performance Trends - Weekly (Mon-Sun, last 12 weeks) */}
      <Card className="rounded-3xl overflow-hidden border border-gray-200/80 min-w-0">
        <div className="p-6 border-b">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-3 lg:space-y-0">
            <div>
              <h2 className="text-lg font-medium">Campaign Performance Trends</h2>
              <p className="text-sm text-gray-500 mt-1">Last 12 weeks (Mon-Sun) — {activeMetric} by Campaign Type</p>
            </div>

            {/* Metrik seçenekleri */}
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
              {["Cost", "Revenue", "Cost+Revenue", "Clicks", "Impressions", "Conversions", "Sessions", "CPC", "CPS", "ROAS"].map((metric) => (
                <button
                  key={`pivot-metric-weekly-${metric}`}
                  onClick={() => setActiveMetric(metric)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    metric === activeMetric
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {metric}
                </button>
              ))}
            </div>
          </div>
          </div>
        <div className="p-0">
          {tableLoading.unifiedGaPerformance ? (
            <div className="loading-logo-container py-16" style={{ minHeight: 'auto' }}>
              <div className="flex flex-col items-center gap-4">
                <img src="/logoicon.png" alt="Loading" className="loading-logo" style={{ width: 48, height: 48 }} />
                <h3 className="text-lg font-medium text-gray-700 mb-1">Loading chart data...</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  Please wait while we prepare the performance chart.
                </p>
              </div>
            </div>
          ) : weeklyPivotTableData.headers.length > 0 ? (
            <div className="overflow-x-auto overflow-y-auto max-h-[420px] w-full relative isolate">
              <table className="text-sm min-w-max">
                <thead className="relative z-[100]">
                  <tr className="bg-gray-50">
                    {weeklyPivotTableData.headers.map((header, index) => (
                      <th key={`pivot-header-weekly-${header}-${index}`} className={`py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[11px] border-r border-dashed border-gray-200 sticky top-0 z-[100] bg-gray-50 ${index === 0 ? 'text-left sticky left-0 min-w-[180px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'text-right min-w-[120px]'} ${index === weeklyPivotTableData.headers.length - 1 ? 'last:border-0 sticky right-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''}`}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {weeklyPivotTableData.rows.map((row, rowIndex) => (
                    <React.Fragment key={`pivot-row-weekly-${row.campaignType}-${rowIndex}`}>
                      <tr className="group hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedWeeklyTypes(prev => ({...prev, [row.campaignType]: !prev[row.campaignType]}))}>
                        <td className="sticky left-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 font-medium text-gray-800 min-w-[180px] whitespace-normal break-words leading-snug border-r border-dashed border-gray-200" title={campaignTypes[row.campaignType]?.label || row.campaignType}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">{expandedWeeklyTypes[row.campaignType] ? '▼' : '▶'}</span>
                            {campaignTypes[row.campaignType]?.label || row.campaignType}
                          </div>
                        </td>
                        {row.values.map((value, colIndex) => (
                          <td key={`pivot-cell-weekly-${rowIndex}-${colIndex}`} className="py-3 px-4 text-right text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                            {['CPC','CPS'].includes(activeMetric) && typeof value !== 'object' && formatCurrencyFixed2(value)}
                            {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(value) : formatNumberCompactShort(value))}
                            {activeMetric === 'Cost+Revenue' && typeof value === 'object' && (
                              <div className="flex items-center justify-end gap-2">
                                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(value.cost)}</span>
                                <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(value.revenue)}</span>
                              </div>
                            )}
                            {activeMetric === 'ROAS' ? 'x' : ''}
                          </td>
                        ))}
                        <td className="sticky right-0 z-[5] bg-white group-hover:bg-gray-50 py-3 px-4 text-right font-bold text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                          {['CPC','CPS'].includes(activeMetric) && typeof row.total !== 'object' && formatCurrencyFixed2(row.total)}
                          {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(row.total) : formatNumberCompactShort(row.total))}
                          {activeMetric === 'Cost+Revenue' && (
                            <div className="flex items-center justify-end gap-2">
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof row.total === 'object' ? row.total.cost : 0))}</span>
                              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof row.total === 'object' ? row.total.revenue : 0))}</span>
                            </div>
                          )}
                          {activeMetric === 'ROAS' ? 'x' : ''}
                        </td>
                      </tr>
                      {expandedWeeklyTypes[row.campaignType] && row.children && row.children.length > 0 && (
                        row.children.map((child, ci) => (
                          <tr key={`pivot-row-weekly-${row.campaignType}-child-${ci}`} className="bg-gray-50">
                            <td className="sticky left-0 z-[5] bg-gray-50 py-2 px-4 text-xs text-gray-700 min-w-[180px] pr-2 border-r border-dashed border-gray-200">
                              <div className="pl-6 whitespace-normal break-words leading-snug">{child.name}</div>
                            </td>
                            {child.values.map((v, vi) => (
                              <td key={`child-val-weekly-${ci}-${vi}`} className="py-2 px-4 text-right text-xs text-gray-700 border-r border-dashed border-gray-200 last:border-0 min-w-[120px]">
                                {['CPC','CPS'].includes(activeMetric) && typeof v !== 'object' && formatCurrencyFixed2(v)}
                                {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(v) : formatNumberCompactShort(v))}
                                {activeMetric === 'Cost+Revenue' && typeof v === 'object' && (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="px-1 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{formatCurrencyCompactShort(v.cost)}</span>
                                    <span className="px-1 py-0.5 rounded bg-green-50 text-green-700 text-[10px]">{formatCurrencyCompactShort(v.revenue)}</span>
                                  </div>
                                )}
                                {activeMetric === 'ROAS' ? 'x' : ''}
                              </td>
                            ))}
                            <td className="sticky right-0 z-[5] bg-gray-50 py-2 px-4 text-right text-xs font-medium text-gray-800 border-r border-dashed border-gray-200 last:border-0 min-w-[120px] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                              {['CPC','CPS'].includes(activeMetric) && typeof child.total !== 'object' && formatCurrencyFixed2(child.total)}
                              {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(child.total) : formatNumberCompactShort(child.total))}
                              {activeMetric === 'Cost+Revenue' && (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px]">{formatCurrencyCompactShort((typeof child.total === 'object' ? child.total.cost : 0))}</span>
                                  <span className="px-1 py-0.5 rounded bg-green-100 text-green-700 text-[10px]">{formatCurrencyCompactShort((typeof child.total === 'object' ? child.total.revenue : 0))}</span>
                                </div>
                              )}
                              {activeMetric === 'ROAS' ? 'x' : ''}
                            </td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="relative z-[100]">
                  <tr className="bg-gray-100 font-bold text-gray-800">
                    <td className="sticky left-0 bottom-0 z-[100] bg-gray-100 py-3 px-4 text-left min-w-[180px] border-r border-dashed border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">Total</td>
                    {weeklyPivotTableData.totals.map((total, index) => (
                      <td key={`pivot-total-weekly-${index}`} className={`py-3 px-4 text-right border-r border-dashed border-gray-200 last:border-0 min-w-[120px] z-[100] bg-gray-100 ${index === weeklyPivotTableData.totals.length - 1 ? 'sticky right-0 bottom-0 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]' : 'sticky bottom-0'}`}>
                        {['CPC','CPS'].includes(activeMetric) && typeof total !== 'object' && formatCurrencyFixed2(total)}
                        {activeMetric !== 'CPC' && activeMetric !== 'CPS' && activeMetric !== 'Cost+Revenue' && (['Cost','Revenue'].includes(activeMetric) ? formatCurrencyCompactShort(total) : formatNumberCompactShort(total))}
                        {activeMetric === 'Cost+Revenue' && (
                          <div className="flex items-center justify-end gap-2">
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof total === 'object' ? total.cost : 0))}</span>
                            <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">{formatCurrencyCompactShort((typeof total === 'object' ? total.revenue : 0))}</span>
                          </div>
                        )}
                        {activeMetric === 'ROAS' ? 'x' : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-gray-400 mb-2">
                <BarChart3 className="w-12 h-12 mx-auto mb-2" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No performance data found for the selected date range.
              </p>
            </div>
          )}
        </div>
      </Card>

          </div>
  );
}       

console.log('GoogleAds sayfası yüklendi - API verilerini kontrol edin');       